import React, { useState, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Drawer,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  useTheme,
  ListItemIcon,
  ListItemText,
  RadioGroup,
  Radio,
  Typography,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  OutlinedInput,
  Box,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MultiIpSshUpload from "./MultiIpSshUpload";
import AwsRoleConnection, { type AwsScope, type RoleTestStatus } from "./AwsRoleConnection";
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ClearIcon from "@mui/icons-material/Clear";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IOSSwitch from "../../../components/IOSSwitch";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { type Dayjs } from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);
import "./NewScanDrawer.scss";
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";
import { toast } from "react-toastify";
import CircularProgress from "@mui/material/CircularProgress";
import SSHService from "../../../shared/hooks/SSHService";
import { useEncryptService, useUTCDate } from "../../../shared/hooks";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import useRolePermissions from "../../../shared/hooks/useRolePermissions";
import {
  resetUserTypeFlags,
  setIsBothCloudAndPremAssetUser,
  setIsNewUserTrue,
  setIsOnlyCloudUser,
  setIsOnlyPremAssetUser,
} from "../../../modules/authentication/login/Login_Details_slice";
import UploadImg from "../../../assets/baseVersion/Upload_img.svg";
import UploadImgDark from "../../../assets/Upload_img_dark.svg";
import UploadService from "../../../shared/UploadService";
import CredentialCreator from "./CredentialCreator";

/* ─── Types ─── */

export type ScanTypeId =
  | "aws-discovery"
  | "aws-discovery-posture"
  | "onprem-discovery"
  | "onprem-discovery-posture"
  | "onprem-posture";

export type ConnectorType = "api" | "ssh" | "windows" | "file-upload";

export interface PostureRow {
  id: string;
  assetIp: string;
  vendorId: string | null;
  categoryId: string | null;
  credentialId: string;
  categories: any[];
  /** Optional master template for On-Prem Posture SSH/API rows */
  templateId?: string | null;
  /** Per-row port (defaults by connector in UI) */
  port?: string;
  newCredUsername?: string;
  newCredName?: string;
  newCredPasswordType?: "password" | "private_key" | "api_token";
  newCredSecret?: string;
  newCredSave?: boolean;
  newCredExecPasswordEnabled?: boolean;
  newCredAutoAssign?: boolean;
  newCredExecPasswordText?: string;
  newCredPrivateKeyFileName?: string;
  newCredPrivateKeyFile?: File | null;
}

/** Endpoint / OS vendors — master template not applicable on New Scan. */
export const isEndpointVendorName = (vendorName?: string | null): boolean => {
  const normalized = String(vendorName || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  if (!normalized) return false;
  const endpoints = [
    "microsoft",
    "windows",
    "ubuntu",
    "debian",
    "redhat",
    "linux",
    "apple",
    "macos",
    "mac",
    "centos",
    "fedora",
    "suse",
    "oraclelinux",
  ];
  return endpoints.some(
    (name) => normalized === name || normalized.startsWith(name)
  );
};

export const ENDPOINT_TEMPLATE_INFO_MESSAGE =
  "Config templates are not available for Windows or Linux devices.";

export const defaultPortForConnector = (
  connector?: ConnectorType | string
): string => {
  if (connector === "api") return "443";
  if (connector === "windows") return "5985";
  return "22";
};

const emptyPostureRow = (
  id?: string,
  _connector?: ConnectorType | string
): PostureRow => ({
  id: id || String(Date.now()),
  assetIp: "",
  vendorId: null,
  categoryId: null,
  credentialId: "",
  categories: [],
  templateId: null,
  // Keep empty; UI shows connector default as placeholder (e.g. 22 for SSH).
  port: "",
});

const normalizeCsvHeader = (header: string) =>
  String(header || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

/** Minimal CSV parser (supports quoted fields). */
const parseCsvText = (text: string): Record<string, string>[] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;
  const input = text.replace(/^\uFEFF/, "");

  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    const next = input[i + 1];
    if (inQuotes) {
      if (ch === '"' && next === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n") {
      row.push(cell);
      cell = "";
      if (row.some((c) => c.trim().length > 0)) rows.push(row);
      row = [];
    } else if (ch !== "\r") {
      cell += ch;
    }
  }
  row.push(cell);
  if (row.some((c) => c.trim().length > 0)) rows.push(row);
  if (rows.length < 2) return [];

  const headers = rows[0].map(normalizeCsvHeader);
  return rows.slice(1).map((cols) => {
    const obj: Record<string, string> = {};
    headers.forEach((h, idx) => {
      obj[h] = String(cols[idx] ?? "").trim();
    });
    return obj;
  });
};

const matchVendorByName = (vendorsList: any[], name?: string) => {
  const needle = String(name || "")
    .trim()
    .toLowerCase();
  if (!needle) return null;
  return (
    vendorsList.find(
      (v: any) => String(v.name || "").trim().toLowerCase() === needle
    ) || null
  );
};

const matchCategoryByName = (categories: any[], name?: string) => {
  const needle = String(name || "")
    .trim()
    .toLowerCase();
  if (!needle) return null;
  return (
    categories.find(
      (c: any) => String(c.name || "").trim().toLowerCase() === needle
    ) || null
  );
};

const csvRowToPostureRow = (
  csvRow: Record<string, string>,
  vendorsList: any[],
  connector?: ConnectorType | string
): PostureRow | null => {
  const assetIp = String(
    csvRow.host || csvRow.ip_address || csvRow.ip || ""
  ).trim();
  if (!assetIp) return null;

  const vendorName = csvRow.vendor || "";
  const categoryName = csvRow.category || "";
  const portRaw = csvRow.custom_port_number || csvRow.port || "";
  const port = /^\d+$/.test(String(portRaw).trim())
    ? String(portRaw).trim()
    : "";

  const vendor = matchVendorByName(vendorsList, vendorName);
  const categories = vendor ? vendor.categories || [] : [];
  const category = matchCategoryByName(categories, categoryName);

  return {
    ...emptyPostureRow(String(Date.now() + Math.random()), connector),
    assetIp,
    vendorId: vendor ? String(vendor.id) : null,
    categoryId: category ? String(category.id) : null,
    categories,
    port,
    credentialId: "",
    templateId: null,
  };
};

interface ScheduleOptions {
  frequency?: any;
  Days?: any;
}

interface NewCredentialPayload {
  username: string;
  name: string;
  /** Primary login secret (password or key-based secret if needed) */
  secret: string;
  passwordType: "password" | "private_key" | "api_token";
  /** Optional exec password for privilege escalation */
  execPassword?: string;
  /** When true, backend can auto-fill exec password from login password */
  autoAssignExecPassword?: boolean;
  /** Optional uploaded key file name for reference */
  privateKeyFileName?: string;
  /** Persist this credential for future scans */
  save: boolean;
  accountId?: string;
  description?: string;
}

export interface NewScanFormData {
  scanName: string;
  description: string;
  scanType: ScanTypeId;
  agentId: string;
  credentialId: string;
  cidrBlocks: string[];
  connectorType?: ConnectorType;
  connectorConfig?: Record<string, string>;
  isScheduled: boolean;
  frequency: string;
  onDate: Dayjs | null;
  atTime: string;
  /** When true, caller can read newCredential to create & attach a fresh credential */
  isNewCredential?: boolean;
  new_cred?: boolean;
  save?: boolean;
  newCredential?: NewCredentialPayload;
  /** Optional schedule payload mirroring legacy New Scan API */
  schedule?: {
    frequency_id?: string | null;
    date?: string;
    day?: string | null;
    time?: string;
  };
}

interface AgentOption {
  id: string;
  name: string;
  agent_type?: "cloud" | "on_prem";
  status_display?: string;
}

interface Credential {
  id: string;
  name: string;
  type?: string;
}

interface PasswordType {
  id: string;
  code: string;
  name: string;
}

interface CredentialType {
  id: string;
  code: string;
  name: string;
  password_type: PasswordType[];
}

interface NewScanDrawerProps {
  open: boolean;
  initialScanType: ScanTypeId;
  onClose: () => void;
  /** Called when "Test" is clicked — wire to your API */
  onTest?: (formData: NewScanFormData) => void;
  /** Called when "Connect" is clicked — wire to your API */
  onConnect?: (formData: NewScanFormData) => void;
  /** Pass agents fetched from API */
  agents?: AgentOption[];
  /** Pass credentials fetched from API */
  credentials?: Credential[];
  /** Called when user selects "Create New Credential" */
  onCreateCredential?: () => void;
  /** Called when "Back" is clicked */
  onBack?: () => void;
  /** Optional initial agent ID to pre-select */
  initialAgentId?: string;
  /** Whether the drawer was opened from the Schedule view */
  isFromScheduleView?: boolean;
  /** Called after a successful Connect/scan submission */
  onConnectSuccess?: () => void;
}

/* ─── Scan-type mapping ─── */

const SCAN_TYPE_OPTIONS: { id: ScanTypeId; label: string }[] = [
  { id: "aws-discovery", label: "AWS Discovery" },
  { id: "aws-discovery-posture", label: "AWS Discovery + Posture" },
  { id: "onprem-discovery-posture", label: "On-Prem Discovery + Posture" },
  { id: "onprem-discovery", label: "On-Prem Discovery" },
  { id: "onprem-posture", label: "On-Prem Posture" },
];

const SCAN_TYPE_DESCRIPTIONS: Record<ScanTypeId, string> = {
  "aws-discovery": "Map & Identify cloud infrastructure assets.",
  "aws-discovery-posture": "Scan and analyse AWS resource configurations",
  "onprem-discovery": "Map & Identify on-prem infrastructure assets.",
  "onprem-discovery-posture": "Internal asset mapping with compliance validation.",
  "onprem-posture": "In-depth security analysis for existing on-prem assets.",
};

const CONNECTOR_OPTIONS: { id: ConnectorType; label: string }[] = [
  { id: "api", label: "API" },
  { id: "ssh", label: "SSH" },
  { id: "windows", label: "Windows" },
  { id: "file-upload", label: "File Upload" },
];

/* ─── MUI shared sx ─── */

/* size="small" already ~40px; do not force height — it breaks the outlined label notch */
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
  },
};

const selectSx = {
  borderRadius: "8px",
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    py: "8.5px",
    lineHeight: 1.4375,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};

const NewScanDrawer: React.FC<NewScanDrawerProps> = ({
  open,
  initialScanType,
  onClose,
  onTest,
  onConnect,
  agents = [],
  credentials: _credentials = [],
  onCreateCredential,
  onBack,
  initialAgentId,
  isFromScheduleView,
  onConnectSuccess,
}) => {

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const modeClass = isDarkMode ? "dark-mode" : "light-mode";
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { doEncrypt, doEncryptLogin } = useEncryptService();
  const { utcObj } = useUTCDate();
  const { hasPermissions } = useRolePermissions();

  const filteredScanTypeOptions = SCAN_TYPE_OPTIONS.filter(option => {
    if (option.id === "aws-discovery-posture") return hasPermissions("aws_discovery_posture");
    if (option.id === "onprem-discovery") return hasPermissions("on_prem_discovery");
    if (option.id === "onprem-posture") return hasPermissions("on_prem_posture");
    if (option.id === "onprem-discovery-posture") return hasPermissions("on_prem_discovery") && hasPermissions("on_prem_posture");
    return true;
  });

  /* ── Core form state ── */
  const [scanName, setScanName] = useState("");
  const [description, setDescription] = useState("");
  const [scanType, setScanType] = useState<ScanTypeId>(initialScanType);
  const [agentId, setAgentId] = useState(initialAgentId || "");
  console.log(agentId, "ageeeeeee");


  const [credentialId, setCredentialId] = useState("");
  /* AWS: connect through a read-only IAM role (recommended, no keys) or with access keys */
  const [awsConnection, setAwsConnection] = useState<"assume_role" | "access_keys">("assume_role");
  const [awsRoleArn, setAwsRoleArn] = useState("");
  // IAM-role scans: one or more accounts, or a whole AWS Organization; one scan per account.
  const [awsAccountIds, setAwsAccountIds] = useState<string[]>([]);
  const [awsScope, setAwsScope] = useState<AwsScope>("accounts");
  const [awsRoleStatus, setAwsRoleStatus] = useState<RoleTestStatus>("idle");


  const [cidrInput, setCidrInput] = useState("");
  const [cidrBlocks, setCidrBlocks] = useState<string[]>([]);
  const [cidrError, setCidrError] = useState("");
  const [chunk, setChunk] = useState("");
  const [chunkError, setChunkError] = useState("");
  const [interval, setInterval] = useState("");
  const [intervalError, setIntervalError] = useState("");
  const [connectorType, setConnectorType] =
    useState<ConnectorType>("api");
  const [connectorConfig, setConnectorConfig] = useState<
    Record<string, string>
  >({});
  const [isScheduled, setIsScheduled] = useState(isFromScheduleView || false);
  const [frequency, setFrequency] = useState("");
  const [onDate, setOnDate] = useState<Dayjs | null>(null);
  const [atTime, setAtTime] = useState("");
  const [testStatus, setTestStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [isConnecting, setIsConnecting] = useState(false);
  const [allAgents, setAllAgents] = useState<AgentOption[]>([]);
  const [isAgentsLoading, setIsAgentsLoading] = useState(false);
  const [scheduleOptions, setScheduleOptions] = useState<ScheduleOptions>({});
  const [scheduleDate, setScheduleDate] = useState("");
  const [selectedDay, setSelectedDay] = useState<string>("");
  const [selectedHour, setSelectedHour] = useState<string>("");
  const [selectedMinute, setSelectedMinute] = useState<string>("");
  const [selectedAmPm, setSelectedAmPm] = useState<string>("");
  const [hourlyInterval, setHourlyInterval] = useState<string>("");
  const [hourlyMinute, setHourlyMinute] = useState<string>("");

  /* ── Local state for inline "Create New Credential" flow ── */
  const [isCreatingCredential, setIsCreatingCredential] = useState(false);
  const [newCredUsername, setNewCredUsername] = useState("");
  const [newCredName, setNewCredName] = useState("");
  const [newCredAccountId, setNewCredAccountId] = useState("");
  const [newCredDescription, setNewCredDescription] = useState("");
  const [newCredPasswordType, setNewCredPasswordType] = useState<
    "password" | "private_key" | "api_token"
  >("password");
  const [newCredSecret, setNewCredSecret] = useState("");  
  const [newCredSave, setNewCredSave] = useState(true);
  const [newCredExecPasswordEnabled, setNewCredExecPasswordEnabled] =
    useState(false);
  const [newCredAutoAssign, setNewCredAutoAssign] = useState(false);
  const [newCredExecPasswordText, setNewCredExecPasswordText] =
    useState("");
  const [newCredShowPassword, setNewCredShowPassword] = useState(false);
  const [newCredShowExecPassword, setNewCredShowExecPassword] =
    useState(false);
  const [newCredPrivateKeyFileName, setNewCredPrivateKeyFileName] =
    useState("");
  const [newCredPrivateKeyFile, setNewCredPrivateKeyFile] = useState<File | null>(null);
  const [newCredPrivateKeyError, setNewCredPrivateKeyError] = useState("");
  const ppkInputRef = useRef<HTMLInputElement | null>(null);

  /* ── Fetched credentials & credential types ── */
  const [sshCredentials, setSshCredentials] = useState<Credential[]>([]);
  const [allCredentialTypes, setAllCredentialTypes] = useState<CredentialType[]>([]);
  const [isCredentialsLoading, setIsCredentialsLoading] = useState(false);

  /* ── On-Prem Posture connector state ── */
  const [assetIp, setAssetIp] = useState("");
  const [_assetIpError, setAssetIpError] = useState("");
  const [customPort, setCustomPort] = useState(false);
  const [customPortNumber, setCustomPortNumber] = useState("");
  const [isBulkUpload, setIsBulkUpload] = useState(false);
  const [vendorsList, setVendorsList] = useState<any[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<string | null>(null);
  const [_categories, _setCategories] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [postureUploadFiles, setPostureUploadFiles] = useState<File[]>([]);
  const [postureUploadError, setPostureUploadError] = useState("");
  const postureUploadInputRef = useRef<HTMLInputElement | null>(null);
  const [isValidatingIp, setIsValidatingIp] = useState(false);
  const cidrInputRef = useRef<HTMLInputElement | null>(null);
  const [isLimitPopupOpen, setIsLimitPopupOpen] = useState(false);
  const [limitPopupMessage, setLimitPopupMessage] = useState("");
  const [bypassLimit, setBypassLimit] = useState(false);
  const [eventId, setEventId] = useState<string | null>(null);
  const [invalidFilesList, setInvalidFilesList] = useState<string[]>([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [accountIdError, setAccountIdError] = useState("");

  /* ── Multi-select credentials for On-Prem Discovery / Discovery+Posture ── */
  const [selectedCredentials, setSelectedCredentials] = useState<string[]>([]);

  /* ── Multi-IP Posture rows state & helpers ── */
  const [isMultiIpMode, setIsMultiIpMode] = useState(true);
  const [isSshUploadMode, setIsSshUploadMode] = useState(false);
  const [postureRows, setPostureRows] = useState<PostureRow[]>([
    emptyPostureRow("1", "api"),
  ]);
  const [masterTemplates, setMasterTemplates] = useState<any[]>([]);
  const addPostureRow = (insertAfterIndex?: number) => {
    setPostureRows((prev) => {
      const newRow = emptyPostureRow(
        String(Date.now() + Math.random()),
        connectorType
      );
      
      if (typeof insertAfterIndex === 'number' && insertAfterIndex >= 0 && insertAfterIndex < prev.length) {
        const newRows = [...prev];
        newRows.splice(insertAfterIndex + 1, 0, newRow);
        return newRows;
      }
      
      return [...prev, newRow];
    });
  };

  const updatePostureRow = (id: string, field: keyof PostureRow, value: any) => {
    if (field === "credentialId" && value === "__create_new__") {
      setIsCreatingCredential(true);
    }
    setPostureRows((prev) => {
      let shouldAddRow = false;
      const newRows = prev.map((row, index) => {
        if (row.id === id) {
          const updatedRow = { ...row, [field]: value };
          if (field === "vendorId") {
            const vendor = vendorsList.find((v: any) => v.id === value);
            updatedRow.categories = vendor ? (vendor.categories || []) : [];
            updatedRow.categoryId = null;
            updatedRow.templateId = null;
          }
          if (field === "categoryId") {
            updatedRow.templateId = null;
          }

          // Auto-add row when the mandatory fields are filled, regardless of order
          if (index === prev.length - 1) {
            let isRowFilled = updatedRow.assetIp.trim().length > 0 && !!updatedRow.credentialId;
            if (connectorType === "api") {
              isRowFilled = isRowFilled && !!updatedRow.vendorId && !!updatedRow.categoryId;
            }
            if (isRowFilled) {
              shouldAddRow = true;
            }
          }

          return updatedRow;
        }
        return row;
      });

      if (shouldAddRow) {
        newRows.push(
          emptyPostureRow(String(Date.now() + Math.random()), connectorType)
        );
      }

      return newRows;
    });
  };

  const removePostureRow = (id: string) => {
    setPostureRows((prev) => {
      const filtered = prev.filter((row) => row.id !== id);
      
      const removedRow = prev.find((row) => row.id === id);
      if (removedRow?.credentialId === "__create_new__" && !filtered.some((r) => r.credentialId === "__create_new__")) {
        setIsCreatingCredential(false);
      }
      
      if (filtered.length === 0) {
        return [emptyPostureRow(undefined, connectorType)];
      }
      return filtered;
    });
  };

  /** Bulk bar: apply credential / template / port to all rows in one go. */
  const applyBulkToAllRows = (opts: {
    credentialId?: string;
    templateId?: string;
    port?: string;
  }) => {
    const tpl =
      opts.templateId !== undefined && opts.templateId
        ? (masterTemplates || []).find(
            (t: any) =>
              String(t.template_id || t.id) === String(opts.templateId)
          )
        : null;

    setPostureRows((prev) =>
      prev.map((row) => {
        const next = { ...row };
        if (opts.credentialId !== undefined) {
          next.credentialId = opts.credentialId;
        }
        if (opts.port !== undefined) {
          next.port = opts.port;
        }
        if (opts.templateId !== undefined) {
          if (!opts.templateId) {
            next.templateId = null;
          } else if (
            tpl &&
            row.vendorId &&
            row.categoryId &&
            String(tpl.vendor_id) === String(row.vendorId) &&
            String(tpl.device_model_id) === String(row.categoryId)
          ) {
            const vendorName =
              vendorsList.find((v: any) => String(v.id) === String(row.vendorId))
                ?.name || "";
            if (!isEndpointVendorName(vendorName)) {
              next.templateId = opts.templateId;
            }
          }
        }
        return next;
      })
    );
  };

  const handlePostureRowKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    rowId: string,
    index: number
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const row = postureRows.find((r) => r.id === rowId);
      const isRowFilled = row ? (row.assetIp.trim().length > 0 && row.credentialId && (connectorType === "api" ? (row.vendorId && row.categoryId) : true)) : false;
      if (isRowFilled) {
        if (index === postureRows.length - 1) {
          addPostureRow();
          // Focus the newly added row after a short delay to allow React to render it
          setTimeout(() => {
            const inputs = document.querySelectorAll('.nsd-multi-ip-cell-ip input');
            if (inputs.length > index + 1) {
              (inputs[index + 1] as HTMLInputElement).focus();
            }
          }, 50);
        } else {
          // If not the last row, just focus the next row
          const inputs = document.querySelectorAll('.nsd-multi-ip-cell-ip input');
          if (inputs.length > index + 1) {
            (inputs[index + 1] as HTMLInputElement).focus();
          }
        }
      }
    }
  };

  const formik = useFormik({
    initialValues: {
      accountId: newCredAccountId,
    },
    validationSchema: Yup.object({
      accountId: Yup.string()
        .matches(/^\d+$/, "Only numbers are allowed")
        .length(12, "Account ID must be exactly 12 digits")
      // .required("Account ID is required"),
    }),
    onSubmit: (values) => {
      console.log("Form submitted", values);
    },
    validateOnChange: true,
    validateOnBlur: true,
  });

  // Sync newCredAccountId with formik if needed, or just use formik.values.accountId
  React.useEffect(() => {
    formik.setFieldValue("accountId", newCredAccountId);
  }, [newCredAccountId]);

  // Reset form state when drawer closes
  React.useEffect(() => {
    if (!open) {
      setScanName("");
      setDescription("");
      setScanType(initialScanType);
      setAgentId(initialAgentId || "");
      setCredentialId("");
      setCidrInput("");
      setCidrBlocks([]);
      setCidrError("");
      setChunk("");
      setChunkError("");
      setInterval("");
      setIntervalError("");
      setConnectorType("api");
      setConnectorConfig({});
      setIsScheduled(isFromScheduleView || false);
      setFrequency("");
      setOnDate(null);
      setAtTime("");
      setTestStatus("idle");
      setIsConnecting(false);
      setScheduleDate("");
      setSelectedDay("");
      setSelectedHour("");
      setSelectedMinute("");
      setSelectedAmPm("");
      setHourlyInterval("");
      setHourlyMinute("");

      setIsCreatingCredential(false);
      setAwsConnection("assume_role");
      setAwsRoleArn("");
      setAwsAccountIds([]);
      setAwsScope("accounts");
      setAwsRoleStatus("idle");
      setNewCredUsername("");
      setNewCredName("");
      setNewCredAccountId("");
      setNewCredDescription("");
      setNewCredPasswordType("password");
      setNewCredSecret("");
      setNewCredSave(true);
      setNewCredExecPasswordEnabled(false);
      setNewCredAutoAssign(false);
      setNewCredExecPasswordText("");
      setNewCredShowPassword(false);
      setNewCredShowExecPassword(false);
      setNewCredPrivateKeyFileName("");
      setNewCredPrivateKeyFile(null);
      setNewCredPrivateKeyError("");

      setAssetIp("");
      setAssetIpError("");
      setCustomPort(false);
      setCustomPortNumber("");
      setIsBulkUpload(false);
      setSelectedVendor(null);
      setSelectedCategory(null);
      setPostureUploadFiles([]);
      setPostureUploadError("");
      setIsValidatingIp(false);
      setIsLimitPopupOpen(false);
      setLimitPopupMessage("");
      setBypassLimit(false);
      setEventId(null);
      setInvalidFilesList([]);
      setIsUploadingFiles(false);
      setAccountIdError("");
      
      setSelectedCredentials([]);
      
      setIsMultiIpMode(true);
      setIsSshUploadMode(false);
      setPostureRows([emptyPostureRow(undefined, "api")]);
      setMasterTemplates([]);
      
      formik.resetForm();
    }
  }, [open, initialScanType, initialAgentId, isFromScheduleView]);

  const isOnPremPosture = scanType === "onprem-posture";
  const isAwsScan =
    scanType === "aws-discovery" || scanType === "aws-discovery-posture";
  // AWS scans are collected by Spark itself (securityscan), so they never use an agent.
  const isAgentUsed = !isAwsScan;
  // AWS through an IAM role: no credential to pick or create, the role test gates the scan.
  const isAwsRole = isAwsScan && awsConnection === "assume_role";
  const isOnPremDiscoveryOrPosture =
    scanType === "onprem-discovery" ||
    scanType === "onprem-discovery-posture" ||
    scanType === "onprem-posture";

  // Fetch vendors when drawer opens
  React.useEffect(() => {
    if (!open) return;
    const fetchVendors = async () => {
      try {
        const res = await axiosInstance.get(END_POINTS.VENDORS);
        const fetchedVendors = res?.data?.data?.vendors || [];
        setVendorsList(fetchedVendors);
      } catch (err) {
        console.error("Error fetching vendors:", err);
      }
    };
    fetchVendors();
  }, [open]);

  // Active + Success master templates for On-Prem Posture SSH/API template picker
  React.useEffect(() => {
    if (!open || !isOnPremPosture) {
      return;
    }
    if (connectorType !== "ssh" && connectorType !== "api") {
      return;
    }
    const fetchTemplates = async () => {
      try {
        const res = await axiosInstance.get(END_POINTS.MASTER_TEMPLATE_LIST, {
          params: { active: true, status: 3 },
        });
        const list =
          res?.data?.data?.templates ||
          res?.data?.data ||
          res?.data?.templates ||
          [];
        setMasterTemplates(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("Error fetching master templates:", err);
        setMasterTemplates([]);
      }
    };
    fetchTemplates();
  }, [open, isOnPremPosture, connectorType]);

  // For On-Prem Posture or AWS: re-fetch credentials based on type/connector
  React.useEffect(() => {
    if (!open || (!isOnPremPosture && !isAwsScan)) return;
    if (isOnPremPosture && connectorType === "file-upload") return;

    const credType = isAwsScan
      ? "aws"
      : connectorType === "windows"
        ? "windows"
        : connectorType === "ssh"
          ? "ssh"
          : "api";

    const fetchPostureCreds = async () => {
      try {
        const response = await axiosInstance.get(
          `${END_POINTS.CREDENTIAL_MGMT}?type=${credType}`
        );
        setCredentials(response.data?.data?.cred_list || []);
      } catch (error) {
        console.error("Error fetching posture credentials:", error);
      }
    };
    fetchPostureCreds();
    // Reset credential selection when connector changes
    setCredentialId("");
    setIsCreatingCredential(false);
  }, [open, connectorType, scanType]);

  // Fetch agents when drawer opens
  React.useEffect(() => {
    if (!open) return;
    const fetchAgents = async () => {
      setIsAgentsLoading(true);
      try {
        const res = await axiosInstance.get(END_POINTS.CLOUD_AGENT);
        const data = res?.data?.data || res?.data || [];
        // API may return an object or array; normalize
        const list: AgentOption[] = Array.isArray(data) ? data : data.agent_list || [];
        setAllAgents(list);
      } catch (err) {
        console.error("Error fetching agents:", err);
      } finally {
        setIsAgentsLoading(false);
      }
    };
    fetchAgents();
  }, [open]);

  // Sync agentId when initialAgentId prop changes (e.g. clicking "Add Scan" on different rows)
  React.useEffect(() => {
    if (open && initialAgentId) {
      setAgentId(initialAgentId);
    }
  }, [open, initialAgentId]);

  // Set isScheduled when opening from schedule view
  React.useEffect(() => {
    if (open) {
      setIsScheduled(isFromScheduleView || false);
    }
  }, [open, isFromScheduleView]);


  // Fetch all credentials (non-posture) when drawer opens
  React.useEffect(() => {
    if (!open) return;
    const fetchCredentials = async () => {
      setIsCredentialsLoading(true);
      try {
        const res = await axiosInstance.get(END_POINTS.CREDENTIAL_MGMT);
        const list = res?.data?.data?.cred_list || res?.data?.data || res?.data || [];
        setSshCredentials(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("Error fetching credentials:", err);
      } finally {
        setIsCredentialsLoading(false);
      }
    };
    fetchCredentials();
  }, [open]);

  // Fetch credential types for resolving credential_type_id
  React.useEffect(() => {
    if (!open) return;
    const fetchCredentialTypes = async () => {
      try {
        const res = await axiosInstance.get(END_POINTS.CREDENTIAL_TYPES);
        const list = res?.data?.data || res?.data || [];
        setAllCredentialTypes(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("Error fetching credential types:", err);
      }
    };
    fetchCredentialTypes();
  }, [open]);

  // Fetch schedule frequency options when drawer opens
  React.useEffect(() => {
    if (!open) return;
    const fetchScheduleOptions = async () => {
      try {
        const response = await axiosInstance.get(END_POINTS.MY_SCAN_SCHEDULE_FREQUENCY);
        const data = response?.data?.data || response?.data || {};
        setScheduleOptions({
          frequency: data.frequency || [],
          Days: data.weekly || [], // API returns 'weekly' not 'Days'
        });
      } catch (err) {
        console.error("Error fetching schedule options:", err);
      }
    };
    fetchScheduleOptions();
  }, [open]);

  /* ── Helpers ── */
  const handlePostureUploadFiles = async (files: FileList | File[]) => {
    const newFiles = Array.from(files);

    if (!newFiles.length) return;

    const allowedTypes = ["text/xml", "application/json", "text/plain", "text/csv", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"];
    const isCsvMode = isMultiIpMode && isSshUploadMode && connectorType !== "file-upload";
    const validFiles = newFiles.filter((file) => {
      const name = file.name.toLowerCase();
      if (isCsvMode) {
        return name.endsWith(".csv");
      }
      return allowedTypes.includes(file.type) || name.endsWith(".xml") || name.endsWith(".json") || name.endsWith(".txt");
    });

    if (newFiles.length > 0 && validFiles.length === 0) {
      const errMsg = isCsvMode ? "Only CSV files are supported." : "Only XML, JSON, and TEXT files are supported.";
      setPostureUploadError(errMsg);
      toast.error(errMsg);
      return;
    }

    // Check for duplicates in all files
    validFiles.forEach((file) => {
      const isDuplicate = postureUploadFiles.some(
        (existingFile) => existingFile.name === file.name
      );
      if (isDuplicate) {
        toast.error(`You have uploaded a duplicate file: ${file.name}`);
      }
    });

    if (validFiles.length > 0) {
      // CSV import always replaces; other modes skip exact-name duplicates.
      const uniqueFiles = isCsvMode
        ? validFiles.slice(0, 1)
        : validFiles.filter(
            (file) =>
              !postureUploadFiles.some(
                (existingFile) => existingFile.name === file.name
              )
          );

      // Only add unique files to the state
      if (uniqueFiles.length > 0) {
        const validation = isCsvMode ? { isValid: true } : UploadService.validateFileUpload(
          postureUploadFiles,
          uniqueFiles,
          false
        );

        if (validation && validation.isValid) {
          if (isCsvMode) {
            const csvFile = uniqueFiles[0];
            setPostureUploadFiles([csvFile]);
            setPostureUploadError("");
            try {
              const text = await csvFile.text();
              const parsed = parseCsvText(text);
              const mapped = parsed
                .map((r) => csvRowToPostureRow(r, vendorsList, connectorType))
                .filter((r): r is PostureRow => Boolean(r));
              if (!mapped.length) {
                setPostureUploadError(
                  "No valid assets found. Need an IP address / host column."
                );
                toast.error(
                  "No valid assets found in CSV. Check the sample format."
                );
                setPostureRows([emptyPostureRow("1", connectorType)]);
              } else {
                setPostureRows(mapped);
                toast.success(
                  `Loaded ${mapped.length} asset${mapped.length > 1 ? "s" : ""} from CSV`
                );
              }
            } catch (err) {
              console.error("CSV parse failed:", err);
              setPostureUploadError("Failed to read CSV file.");
              toast.error("Failed to read CSV file.");
            }
          } else {
            setPostureUploadFiles((prev) => [...prev, ...uniqueFiles]);
            setPostureUploadError("");
          }

          // Immediate upload to validate content
          if (!isCsvMode) {
            setIsUploadingFiles(true);
            try {
              const formData = new FormData();
              uniqueFiles.forEach((f) => {
                formData.append("resumes", f, f.name);
              });
              formData.append("save", "false");
              if (eventId) {
                formData.append("event_id", eventId);
              }

              const response = await axiosInstance.post(
                END_POINTS.FILECHUNKS,
                formData,
                {
                  headers: {
                    "Content-Type": "multipart/form-data",
                  },
                }
              );

              const uploadData = response?.data?.data || response?.data;
              const newEventId =
                uploadData?.event_id ||
                uploadData?.file_data?.[0]?.event_id ||
                response?.data?.event_id;

              if (newEventId) {
                setEventId(newEventId);
              }

              const newInvalidFiles = uploadData?.invalid_files || [];
              if (newInvalidFiles.length > 0) {
                setInvalidFilesList((prev) => [...prev, ...newInvalidFiles]);
                toast.error(
                  "You have uploaded invalid files, please remove them and try submitting again."
                );
              }
            } catch (error) {
              console.error("Error during immediate file validation:", error);
              toast.error("Failed to validate uploaded files.");
            } finally {
              setIsUploadingFiles(false);
            }
          }
        }
      }
    }
  };

  const handlePostureUploadInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files) {
      handlePostureUploadFiles(event.target.files);
      // reset so the same file can be chosen again if needed
      event.target.value = "";
    }
  };

  const handlePostureUploadDrop = (
    event: React.DragEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      handlePostureUploadFiles(event.dataTransfer.files);
      event.dataTransfer.clearData();
    }
  };

  const handlePostureUploadDragOver = (
    event: React.DragEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handlePostureChooseFilesClick = () => {
    postureUploadInputRef.current?.click();
  };

  const handleScheduleDateChange = (newDate: Dayjs | null) => {
    setOnDate(newDate);

    if (!newDate) {
      setScheduleDate("");
      return;
    }

    const day = String(newDate.date()).padStart(2, "0");
    const month = String(newDate.month() + 1).padStart(2, "0");
    const year = newDate.year();
    setScheduleDate(`${day}-${month}-${year}`);

    const today = dayjs().startOf("day");
    const isToday = newDate.isSame(today, "day");

    if (isToday) {
      const currentTime = new Date();
      currentTime.setMinutes(currentTime.getMinutes() + 5);

      const hour = currentTime.getHours();
      const minute = currentTime.getMinutes();
      const period = hour < 12 ? "AM" : "PM";
      const displayHour = (hour % 12 || 12).toString();
      const displayMinute = minute.toString().padStart(2, "0");

      setSelectedHour(displayHour);
      setSelectedMinute(displayMinute);
      setSelectedAmPm(period);
      setAtTime(`${displayHour}:${displayMinute} ${period}`);
    } else {
      setSelectedHour("12");
      setSelectedMinute("00");
      setSelectedAmPm("PM");
      setAtTime("12:00 PM");
    }
  };

  const getFormData = (): NewScanFormData => {
    const freqObj = scheduleOptions.frequency?.find(
      (f: any) => f.id === frequency
    );
    const isHourlyFreq = freqObj?.name === "Hourly";
    const timeValue = isHourlyFreq
      ? `${hourlyInterval}:${hourlyMinute} ${selectedAmPm}`
      : atTime;

    const schedule = isScheduled
      ? {
        frequency_id: frequency || null,
        date: isHourlyFreq ? undefined : (scheduleDate || undefined),
        day: selectedDay || undefined,
        time: timeValue,
      }
      : undefined;

    const combinedIpRanges = [
      ...cidrBlocks,
      ...cidrInput.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
    ];

    return {
      scanName,
      description,
      scanType,
      agentId,
      credentialId,
      cidrBlocks: combinedIpRanges,
      connectorType,
      connectorConfig,
      isScheduled,
      frequency,
      onDate,
      atTime: timeValue,
      isNewCredential: isCreatingCredential,
      new_cred: isCreatingCredential,
      save: isCreatingCredential ? newCredSave : false,
      newCredential: isCreatingCredential
        ? {
          username: newCredUsername.trim(),
          name: newCredName.trim(),
          accountId: newCredAccountId.trim(),
          description: newCredDescription.trim(),
          secret: newCredSecret,
          passwordType: newCredPasswordType,
          execPassword: newCredExecPasswordText,
          autoAssignExecPassword: newCredAutoAssign,
          privateKeyFileName: newCredPrivateKeyFileName,
          save: newCredSave,
        }
        : undefined,
      schedule,
    };
  };

  const resetFormOnClose = () => {
    // Reset the scan type to the initial value and clear all associated fields
    handleScanTypeChange(initialScanType);

    // Ensure connection-specific states are also cleared
    setIsConnecting(false);
    setTestStatus("idle");
    setEventId(null);
    setInvalidFilesList([]);
    setIsUploadingFiles(false);
  };

  const handleClose = () => {
    resetFormOnClose();
    onClose();
  };

  const handleTest = async () => {
    if (!onTest) {
      return;
    }
    setTestStatus("loading");
    try {
      await Promise.resolve(onTest(getFormData()));
      setTestStatus("success");
    } catch (error) {
      setTestStatus("error");
    }
  };

  // Validate Asset IP/URL
  const ipRegex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9\-]+\.)+[a-zA-Z]{2,6}(\/[^\s]*)?$/;

  const _handleAssetIpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAssetIp(value);
    if (value.trim() && !ipRegex.test(value) && !urlRegex.test(value)) {
      setAssetIpError("Invalid IP address or URL format");
    } else {
      setAssetIpError("");
    }
  };
  // Mark handlers as used to avoid unused-variable errors
  void _handleAssetIpChange;

  const _handleVendorChange = (event: any) => {
    const value = event.target.value;
    setSelectedVendor(value === "" ? null : value);
    if (value) {
      const vendor = vendorsList.find((v: any) => v.id === value);
      if (vendor) {
        _setCategories(vendor.categories || []);
        setSelectedCategory(null);
      }
    } else {
      _setCategories([]);
      setSelectedCategory(null);
    }
  };
  // Mark handlers as used to avoid unused-variable errors
  void _handleVendorChange;

  // Vendor filtering removed — vendorsList is kept for future use

  const handleOnPremPostureFileUploadConnect = async () => {
    if (!postureUploadFiles.length) {
      toast.error("Please upload at least one file.");
      return;
    }

    if (invalidFilesList.length > 0) {
      toast.error(
        "You have uploaded invalid files, please remove them and try submitting again."
      );
      return;
    }

    if (!eventId) {
      toast.error("No valid event ID found. Please re-upload your files.");
      return;
    }

    setIsConnecting(true);
    try {
      let processRequest: any = {
        event_id: eventId,
        deleted_data: [],
      };

      if (scanName.trim()) {
        processRequest.name = scanName.trim();
      }
      if (description.trim()) {
        processRequest.description = description.trim();
      }

      if (credentialId) {
        processRequest.cred_id = credentialId;
        const isCreatingCred = credentialId === "__create_new__";
        processRequest.new_cred = isCreatingCred;
        processRequest.save = isCreatingCred ? (isScheduled ? true : newCredSave) : false;
        
        if (isCreatingCred) {
          processRequest.cred_name = newCredName.trim();
          processRequest.username = doEncryptLogin(newCredUsername.trim());
          processRequest.api_token = doEncryptLogin(newCredSecret);
          processRequest.loginParameter = newCredPasswordType;
          if (newCredPasswordType === "password") {
            processRequest.password = doEncryptLogin(newCredSecret);
          } else {
            processRequest.private_key_name = newCredPrivateKeyFileName;
            processRequest.private_key = newCredPrivateKeyFile;
          }
          processRequest.auto_assign = newCredAutoAssign;
          processRequest.exec_password = newCredExecPasswordEnabled;
          if (newCredExecPasswordEnabled && newCredExecPasswordText) {
            processRequest.enable_password = doEncryptLogin(newCredExecPasswordText);
          }
          if (allCredentialTypes.length > 0) {
            let typeCode = connectorType || "ssh";
            if (connectorType === "windows" || connectorType === "winRM") typeCode = "windows";
            else if (connectorType === "api") typeCode = "api";
            
            const matchingType = allCredentialTypes.find((t) => t.code === typeCode) || allCredentialTypes[0];
            if (matchingType?.password_type) {
              const pwType = matchingType.password_type.find((pt) => pt.code === newCredPasswordType);
              if (pwType) processRequest.credential_type_id = pwType.id;
            }
          }
        }
      }

      let finalData = processRequest;
      let config = {};
      if (processRequest.new_cred && processRequest.loginParameter === "private_key" && processRequest.private_key) {
        const formData = new FormData();
        Object.entries(processRequest).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              value.forEach(v => formData.append(key, v));
            } else {
              formData.append(key, value as any);
            }
          }
        });
        finalData = formData;
        config = { headers: { "Content-Type": "multipart/form-data" } };
      }

      const processResponse = await axiosInstance.post(
        END_POINTS.FILE_UPLOAD_PROCESS,
        finalData,
        config
      );

      if (processResponse.status === 200) {
        toast.success(
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <span>{processResponse.data?.message || "Scan submitted successfully"}</span>
            <span
              style={{ cursor: "pointer", color: "#3f51b5", textDecoration: "underline", marginLeft: "10px", whiteSpace: "nowrap" }}
              onClick={() => navigate("/on-premises/scans")}
            >
              View Scan
            </span>
          </div>
        );
        await updateAssetCount();
        handleClose();
      } else {
        throw new Error(
          processResponse.data?.message || "Failed to process uploaded files"
        );
      }
    } catch (error: any) {
      console.error("Error in On-Prem Posture file upload:", error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit scan";
      toast.error(message);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleConnect = async () => {
    // Special case: On-Prem Posture with File Upload connector uses
    // the multi-file upload + process-uploads flow, same as old New Scan
    if (isOnPremPosture && connectorType === "file-upload") {
      await handleOnPremPostureFileUploadConnect();
      return;
    }

    if (isOnPremPosture && isMultiIpMode) {
      setIsConnecting(true);
      try {
        const payload: Record<string, any> = {
          scan_type: "onprem_posture",
          agent_id: agentId,
          scanname: scanName.trim(),
          description: description.trim(),
          scan_method: connectorType,
          schedule: isScheduled,
        };

        // Manual entry and CSV-imported assets both submit as devices[].
        payload.devices = [];

        if (isScheduled) {
          payload.frequency_id = frequency;
          const freqObj = scheduleOptions.frequency?.find(
            (f: any) => f.id === frequency
          );
          const isHourlyFreq = freqObj?.name === "Hourly";
          const rawTime = isHourlyFreq
            ? `${hourlyInterval}:${hourlyMinute} ${selectedAmPm}`
            : atTime;

          let schedObj = {
            date: scheduleDate,
            time: rawTime,
            day: selectedDay,
            frequency_id: frequency,
          };

          schedObj = utcObj(schedObj);

          if (!isHourlyFreq) {
            payload.date = schedObj.date;
          }
          payload.time = schedObj.time;
          payload.day = schedObj.day;
          payload.frequency_id = schedObj.frequency_id;
        }

        let hasFiles = false;

        // CSV import and manual entry both submit devices[] from the table.
        const rowsToSubmit = postureRows.filter(
          (row) => row.assetIp.trim().length > 0 && row.credentialId
        );
        rowsToSubmit.forEach((row) => {
          const isRowCreatingCred = row.credentialId === "__create_new__";
          const rowPort = String(row.port || "").trim();
          const effectivePort =
            rowPort ||
            String(customPortNumber || "").trim() ||
            defaultPortForConnector(connectorType);
          const deviceObj: Record<string, any> = {
            host: row.assetIp.trim(),
            scan_method: connectorType,
            custom_port_number: effectivePort,
            custom_port: true,
            vendor: row.vendorId,
            category_id: row.categoryId,
            new_cred: isRowCreatingCred,
            save: isRowCreatingCred
              ? isScheduled
                ? true
                : (row.newCredSave ?? newCredSave)
              : false,
          };
          if (row.templateId) {
            deviceObj.template_id = row.templateId;
          }

          if (!isRowCreatingCred) {
            deviceObj.cred_id = row.credentialId;
          } else {
            const rPwType = row.newCredPasswordType ?? newCredPasswordType;
            deviceObj.cred_name = (row.newCredName ?? newCredName).trim();
            deviceObj.username = doEncryptLogin(
              (row.newCredUsername ?? newCredUsername).trim()
            );
            deviceObj.api_token = doEncryptLogin(
              row.newCredSecret ?? newCredSecret
            );
            deviceObj.loginParameter = rPwType;
            if (rPwType === "password") {
              deviceObj.password = doEncryptLogin(
                row.newCredSecret ?? newCredSecret
              );
            } else {
              deviceObj.private_key_name =
                row.newCredPrivateKeyFileName ?? newCredPrivateKeyFileName;
              deviceObj.private_key =
                row.newCredPrivateKeyFile !== undefined
                  ? row.newCredPrivateKeyFile
                  : newCredPrivateKeyFile;
              if (deviceObj.private_key) {
                hasFiles = true;
              }
            }
            deviceObj.auto_assign =
              row.newCredAutoAssign ?? newCredAutoAssign;
            deviceObj.exec_password =
              row.newCredExecPasswordEnabled ?? newCredExecPasswordEnabled;
            const rExecText =
              row.newCredExecPasswordText ?? newCredExecPasswordText;
            if (deviceObj.exec_password && rExecText) {
              deviceObj.enable_password = doEncryptLogin(rExecText);
            }
            if (allCredentialTypes.length > 0) {
              let typeCode = connectorType || "ssh";
              if (connectorType === "windows" || connectorType === "winRM")
                typeCode = "windows";
              else if (connectorType === "api") typeCode = "api";

              const matchingType =
                allCredentialTypes.find((t) => t.code === typeCode) ||
                allCredentialTypes[0];
              if (matchingType?.password_type) {
                const pwType = matchingType.password_type.find(
                  (pt) => pt.code === rPwType
                );
                if (pwType) deviceObj.credential_type_id = pwType.id;
              }
            }
          }

          payload.devices.push(deviceObj);
        });

        console.log("OnPrem Posture Multi-IP Payload:", JSON.parse(JSON.stringify(payload, (key, value) => {
          if (value instanceof File) return value.name;
          return value;
        })));
        
        let finalData: any = payload;
        let config = {};

        if (hasFiles) {
          const formData = new FormData();
          Object.entries(payload).forEach(([key, value]) => {
            if (key === "devices") {
              payload.devices.forEach((device: any, index: number) => {
                Object.entries(device).forEach(([dKey, dValue]) => {
                  if (dValue !== undefined && dValue !== null) {
                    formData.append(
                      `devices[${index}][${dKey}]`,
                      dValue as any
                    );
                  }
                });
              });
            } else if (value !== undefined && value !== null) {
              formData.append(key, value as any);
            }
          });
          finalData = formData;
          config = { headers: { "Content-Type": "multipart/form-data" } };
        }

        const response = await axiosInstance.post(END_POINTS.NEW_MULTI_SCAN, finalData, config);
        if (response.status === 200 || response.status === 201) {
          toast.success(
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <span>All scans submitted successfully</span>
              <span
                style={{ cursor: "pointer", color: "#3f51b5", textDecoration: "underline", marginLeft: "10px", whiteSpace: "nowrap" }}
                onClick={() => navigate("/on-premises/scans")}
              >
                View Scans
              </span>
            </div>
          );
          await updateAssetCount();
          onConnectSuccess?.();
          handleClose();
        }
      } catch (error: any) {
        console.error("Error submitting scans:", error);
        toast.error(error?.response?.data?.message || "Failed to submit one or more scans");
      } finally {
        setIsConnecting(false);
      }
      return;
    }

    // For on-prem or AWS scans – build payload and submit to secure-scan/new-scan
    if (isOnPremDiscoveryOrPosture || isAwsScan) {
      setIsConnecting(true);
      try {
        const payload: Record<string, any> = {};

        // Map internal scan IDs to the backend's expected names
        const scanTypeMap: Record<string, string> = {
          "onprem-discovery-posture": "onprem_discovery_posture",
          "onprem-discovery": "onprem_discovery",
          "onprem-posture": "onprem_posture",
          "aws-discovery": "aws_discovery",
          "aws-discovery-posture": "aws_discovery_posture",
        };

        const formData = getFormData();
        const combinedIpRanges = formData.cidrBlocks;

        payload.scan_type = scanTypeMap[scanType] || scanType;
        if (isAwsScan) {
          // payload.scan_method = "aws_credentials";
          payload.account_type = "single";
          payload.cloud_account_type = "b242b762-cc66-402c-91f1-13d98a672a7f";
          payload.account_id = newCredAccountId.trim()
          // An IAM-role account sends no keys: Spark assumes the role it tested.
          payload.connection_method = isAwsRole ? "assume_role" : "access_keys";
        }
        if (isAgentUsed) payload.agent_id = agentId;
        payload.scanname = scanName.trim();
        payload.description = description.trim();
        if (scanType === "onprem-discovery" || scanType === "onprem-discovery-posture") {
          payload.interval = interval || "1";
          payload.chunk_size = chunk || "16";
          payload.cred_ids = selectedCredentials
        }

        if (scanType === "onprem-posture") {
          payload.host = doEncryptLogin(combinedIpRanges[0] || assetIp || "");
          payload.agent_id = agentId;
          payload.scan_method = connectorType;
          payload.custom_port_number = customPortNumber;
          payload.custom_port = customPort;
          payload.vendor = selectedVendor;
          payload.category_id = selectedCategory;

          if (!isCreatingCredential) {
            payload.cred_id = credentialId;
            payload.agent_id = agentId;

          }
        } else {
          // Discovery and Discovery+Posture types (on-prem + AWS)
          payload.ip_range = combinedIpRanges;
          // AWS: omit agent_id when none selected; on-prem still sends it
          if (!isAwsScan || agentId) {
            payload.agent_id = agentId;
          }

          // Discovery and Discovery+Posture scans now also require credentials
          if (!isCreatingCredential) {
            payload.cred_id = credentialId;
          }

          // Automatically validate IP ranges if not already bypassed
          if ((scanType === "onprem-discovery" || scanType === "onprem-discovery-posture") && !bypassLimit) {
            const isValid = await handleValidateIp(true);
            if (!isValid) {
              setIsConnecting(false);
              return;
            }
          }
        }

        // Handle Scheduling
        if (isScheduled) {
          payload.schedule = true;
          payload.frequency_id = frequency;

          // Construct time in H:MM format (24-hour)
          const freqObj = scheduleOptions.frequency?.find(
            (f: any) => f.id === frequency
          );
          const isHourlyFreq = freqObj?.name === "Hourly";

          if (!isHourlyFreq) {
            payload.date = scheduleDate; // Formatted as DD-MM-YYYY with leading zeros in handleScheduleDateChange
          }

          const rawTime = isHourlyFreq
            ? `${hourlyInterval}:${hourlyMinute} ${selectedAmPm}`
            : atTime;

          let schedObj = {
            date: scheduleDate,
            time: rawTime,
            day: selectedDay,
            frequency_id: frequency,
          };

          // Apply UTC transformation
          schedObj = utcObj(schedObj);

          if (!isHourlyFreq) {
            payload.date = schedObj.date;
          } else {
            delete payload.date; // Ensure date is not sent for hourly frequency
          }
          payload.time = schedObj.time;
          payload.day = schedObj.day;
          payload.frequency_id = schedObj.frequency_id;
        } else {
          payload.schedule = false;
        }

        // Credential handling (supporting inline creation if needed, though primarily using cred_id)
        payload.new_cred = isCreatingCredential;
        payload.save = isCreatingCredential ? (isScheduled ? true : newCredSave) : false;
        if (isCreatingCredential) {
          payload.cred_name = newCredName.trim();
          if (!isAwsScan) {
            payload.username = doEncryptLogin(newCredUsername.trim());
            payload.api_token = doEncryptLogin(newCredSecret)

          }
          payload.loginParameter = newCredPasswordType;
          if (isAwsScan) {

            payload.account_id = newCredAccountId.trim();
            payload.access_key = doEncryptLogin(newCredUsername.trim());
            payload.secret_key = doEncryptLogin(newCredSecret);
            payload.encrypt = true;
            payload.global_cred = true;
            payload.scan_method = "aws_credentials";
            if (newCredDescription.trim()) {
              payload.description = newCredDescription.trim();
            }
          }
          if (!isAwsScan) {
            if (newCredPasswordType === "password") {
              payload.password = doEncryptLogin(newCredSecret);
            } else {
              payload.private_key_name = newCredPrivateKeyFileName;
              payload.private_key = newCredPrivateKeyFile;
            }
            payload.auto_assign = newCredAutoAssign;
            payload.exec_password = newCredExecPasswordEnabled;
            if (newCredExecPasswordEnabled && newCredExecPasswordText) {
              payload.enable_password = doEncryptLogin(newCredExecPasswordText);
            }
          }
          if (allCredentialTypes.length > 0) {
            let typeCode = connectorType || "ssh";
            if (isAwsScan) {
              typeCode = "aws";
            } else if (connectorType === "windows" || connectorType === "winRM") {
              typeCode = "windows";
            } else if (connectorType === "api") {
              typeCode = "api";
            }
            const matchingType =
              allCredentialTypes.find((t) => t.code === typeCode) ||
              allCredentialTypes[0];
            if (matchingType?.password_type) {
              const targetPwTypeCode = isAwsScan ? "secrets" : newCredPasswordType;
              const pwType = matchingType.password_type.find(
                (pt) => pt.code === targetPwTypeCode
              );
              if (pwType) {
                payload.credential_type_id = pwType.id;
                // payload.agent_id = agentId;
              }
            }
          }
        }

        // IAM role: one scan per account, started one after another; each runs on its own.
        if (isAwsRole) {
          await submitAwsRoleScans(payload);
          return;
        }

        console.log("Submitting on-prem scan payload:", payload);

        // Convert to FormData if we have a file upload for the private key
        let finalData: any = payload;
        let config = {};
        if (isCreatingCredential && newCredPasswordType === "private_key" && newCredPrivateKeyFile) {
          const formData = new FormData();
          Object.entries(payload).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              if (Array.isArray(value)) {
                value.forEach(v => formData.append(key, v));
              } else {
                formData.append(key, value as any);
              }
            }
          });
          finalData = formData;
          config = { headers: { "Content-Type": "multipart/form-data" } };
        }

        console.log("Scan Submission Payload:", finalData instanceof FormData ? Object.fromEntries(finalData.entries()) : finalData);
        const response = await axiosInstance.post(
          END_POINTS.NEW_SCAN_TYPES,
          finalData,
          config
        );

        if (response?.status === 200 || response?.status === 201) {
          toast.success(
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <span>{response.data?.message || "Scan submitted successfully"}</span>
              <span
                style={{ cursor: "pointer", color: "#3f51b5", textDecoration: "underline", marginLeft: "10px", whiteSpace: "nowrap" }}
                onClick={() => navigate("/on-premises/scans")}
              >
                View Scan
              </span>
            </div>
          );
          await updateAssetCount();
          onConnectSuccess?.();
          handleClose();
        }
      } catch (error: any) {
        console.error("Error submitting scan:", error);
        toast.error(error?.response?.data?.message || "Failed to submit scan");
      } finally {
        setIsConnecting(false);
      }
      return;
    }

    // For non on-prem scans – use original onConnect callback
    if (!onConnect) {
      return;
    }
    setIsConnecting(true);
    try {
      await Promise.resolve(onConnect(getFormData()));
    } finally {
      setIsConnecting(false);
    }
  };

  /**
   * IAM-role AWS scans: the same request once per account. With several accounts each scan is
   * named after its account. Accounts that could not be started stay in the list to retry.
   */
  const submitAwsRoleScans = async (base: Record<string, unknown>) => {
    const ids = [...awsAccountIds];
    const baseName = String(base.scanname || "").slice(0, 280);
    const failed: string[] = [];
    for (const id of ids) {
      const body = { ...base, account_id: id, scanname: ids.length > 1 ? `${baseName} - ${id}` : baseName };
      try {
        const response = await axiosInstance.post(END_POINTS.NEW_SCAN_TYPES, body);
        if (response?.status !== 200 && response?.status !== 201) failed.push(id);
      } catch (error) {
        console.error("Error submitting AWS scan for account", id, error);
        failed.push(id);
      }
    }
    const started = ids.length - failed.length;
    if (started === 0) {
      toast.error(
        ids.length === 1
          ? "We couldn't start the scan. Please try again in a moment."
          : "We couldn't start the scans. Please try again in a moment."
      );
      return;
    }
    const viewScans = (
      <span
        style={{ cursor: "pointer", color: "#3f51b5", textDecoration: "underline", marginLeft: "10px", whiteSpace: "nowrap" }}
        onClick={() => navigate("/on-premises/scans")}
      >
        View Scan{started === 1 ? "" : "s"}
      </span>
    );
    await updateAssetCount();
    onConnectSuccess?.();
    if (failed.length > 0) {
      toast.warning(
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
          <span>
            Started {started} of {ids.length} scans. The {failed.length === 1 ? "account" : "accounts"} still in the
            list couldn't be started; try again.
          </span>
          {viewScans}
        </div>
      );
      setAwsAccountIds(failed);
      return;
    }
    toast.success(
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
        <span>{ids.length === 1 ? "Scan submitted successfully" : `Started ${ids.length} scans, one per account`}</span>
        {viewScans}
      </div>
    );
    handleClose();
  };

  /** After successful scan, update Redux user type flags (same as old NewScan) */
  const updateAssetCount = async () => {
    try {
      dispatch(resetUserTypeFlags());
      const res = await axiosInstance.get(END_POINTS.IS_NEW_USER);
      if (res.status === 200) {
        dispatch(
          setIsOnlyPremAssetUser({ isOnlyPremAssetUser: false })
        );
        dispatch(setIsOnlyCloudUser({ isOnlyCloudUser: false }));
        dispatch(
          setIsBothCloudAndPremAssetUser({
            isBothCloudAndPremAssetUser: false,
          })
        );
        const parsed = res?.data?.data || false;
        if (parsed.device_count === 0 && parsed.cloud_count === 0) {
          dispatch(setIsNewUserTrue({ isNewUserTrue: true }));
        } else if (
          parsed.device_count > 0 &&
          parsed.cloud_count === 0
        ) {
          dispatch(
            setIsOnlyPremAssetUser({ isOnlyPremAssetUser: true })
          );
        } else if (
          parsed.device_count === 0 &&
          parsed.cloud_count > 0
        ) {
          dispatch(setIsOnlyCloudUser({ isOnlyCloudUser: true }));
        } else if (
          parsed.device_count > 0 &&
          parsed.cloud_count > 0
        ) {
          dispatch(
            setIsBothCloudAndPremAssetUser({
              isBothCloudAndPremAssetUser: true,
            })
          );
        }
      }
    } catch (error) {
      console.error("Error updating asset count:", error);
    }
  };

  const resetNewCredentialFields = (opts?: { preserveAccountId?: boolean }) => {
    setNewCredUsername("");
    setNewCredName("");
    // AWS form Account ID is a separate field — don't wipe it on credential change
    if (!opts?.preserveAccountId) {
      setNewCredAccountId("");
    }
    setNewCredDescription("");
    setNewCredPasswordType("password");
    setNewCredSecret("");
    setNewCredSave(true);
    setNewCredExecPasswordEnabled(false);
    setNewCredAutoAssign(false);
    setNewCredExecPasswordText("");
    setNewCredShowPassword(false);
    setNewCredShowExecPassword(false);
    setNewCredPrivateKeyFileName("");
    setNewCredPrivateKeyFile(null);
    setNewCredPrivateKeyError("");
    if (ppkInputRef.current) {
      ppkInputRef.current.value = "";
    }
  };

  const handleCredentialChange = (value: string) => {
    const preserveAccountId = isAwsScan;
    if (value === "__create_new__") {
      // Show inline create-credential section by default
      setIsCreatingCredential(true);
      setCredentialId("__create_new__");
      resetNewCredentialFields({ preserveAccountId });
      setTestStatus("idle");
      // Optional external handler (e.g. open a separate drawer) can still run
      onCreateCredential?.();
      return;
    }
    // Switching back to an existing credential hides the inline creator
    setIsCreatingCredential(false);
    setCredentialId(value);
    resetNewCredentialFields({ preserveAccountId });
    setTestStatus("idle");
  };

  const handleAgentChange = (value: string) => {
    setAgentId(value);
    setTestStatus("idle");
  };

  const handleScanTypeChange = (value: ScanTypeId) => {
    setScanType(value);
    setTestStatus("idle");

    // Reset core fields
    setScanName("");
    setDescription("");

    // Reset agent
    if (!location.pathname.includes("organization/agent")) {
      setAgentId("");
    } else if (initialAgentId) {
      setAgentId(initialAgentId);
    }

    // Set default connector type
    setConnectorType("api");

    // Reset CIDR / discovery fields
    setCidrInput("");
    setCidrBlocks([]);
    setCidrError("");
    setChunk("");
    setChunkError("");
    setInterval("");
    setIntervalError("");
    setBypassLimit(false);

    // Reset scheduling
    setIsScheduled(isFromScheduleView || false);
    setFrequency("");
    setOnDate(null);
    setAtTime("");
    setScheduleDate("");
    setSelectedDay("");
    setSelectedHour("");
    setSelectedMinute("");
    setSelectedAmPm("");
    setHourlyInterval("");
    setHourlyMinute("");

    // Reset credentials
    setCredentialId("");
    if (value === "aws-discovery" || value === "aws-discovery-posture") {
      setIsCreatingCredential(true);
      setCredentialId("__create_new__");
    } else {
      setIsCreatingCredential(false);
    }
    resetNewCredentialFields();

    // Reset posture-specific fields
    setAssetIp("");
    setAssetIpError("");
    setCustomPort(false);
    setCustomPortNumber("");
    setIsBulkUpload(false);
    setSelectedVendor(null);
    _setCategories([]);
    setSelectedCategory(null);
    setPostureUploadFiles([]);
    setPostureUploadError("");
    setSelectedCredentials([]);
    setIsMultiIpMode(true);
    setPostureRows([emptyPostureRow("1", connectorType)]);
    if (postureUploadInputRef.current) {
      postureUploadInputRef.current.value = "";
    }
  };

  const handleConnectorChange = (value: ConnectorType) => {
    setConnectorType(value);
    setTestStatus("idle");

    if (scanType === "onprem-posture" && value === "file-upload") {
      setIsScheduled(false);
    } else {
      setIsScheduled(isFromScheduleView || false);
    }

    // Reset agent
    if (!location.pathname.includes("organization/agent")) {
      setAgentId("");
    } else if (initialAgentId) {
      setAgentId(initialAgentId);
    }

    // Reset credential fields
    setCredentialId("");
    setIsCreatingCredential(false);
    resetNewCredentialFields();

    // Reset posture-specific fields
    setAssetIp("");
    setAssetIpError("");
    setCustomPort(false);
    setCustomPortNumber("");
    setSelectedVendor(null);
    _setCategories([]);
    setSelectedCategory(null);
    setPostureUploadFiles([]);
    setPostureUploadError("");
    setIsMultiIpMode(true);
    setPostureRows([emptyPostureRow("1", value)]);
    if (postureUploadInputRef.current) {
      postureUploadInputRef.current.value = "";
    }

    // Reset CIDR fields
    setCidrInput("");
    setCidrBlocks([]);
    setCidrError("");
    setChunk("");
    setChunkError("");
    setInterval("");
    setIntervalError("");
    setBypassLimit(false);
  };

  const updateConnectorField = (key: string, value: string) => {
    setConnectorConfig((prev) => ({ ...prev, [key]: value }));
    setTestStatus("idle");
  };

  const addCidrFromInput = () => {
    const raw = cidrInput.trim();
    if (!raw) return;

    const parts = raw
      .split(/[\n,]/)
      .map((p) => p.trim())
      .filter(Boolean);

    if (!parts.length) return;

    setCidrBlocks((prev) => {
      const next = [...prev];
      parts.forEach((p) => {
        if (!next.includes(p)) {
          next.push(p);
        }
      });
      return next;
    });

    setCidrInput("");
    setTestStatus("idle");
    setCidrError("");
    setBypassLimit(false);
  };

  const handleCidrKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addCidrFromInput();
    } else if (
      (event.key === "Backspace" || event.key === "Delete") &&
      !cidrInput &&
      cidrBlocks.length
    ) {
      // Remove last block when input is empty
      setCidrBlocks((prev) => prev.slice(0, -1));
      setTestStatus("idle");
      setCidrError("");
      setBypassLimit(false);
    }
  };

  const handleRemoveCidr = (block: string) => {
    setCidrBlocks((prev) => prev.filter((b) => b !== block));
    setTestStatus("idle");
    setCidrError("");
    setBypassLimit(false);
  };

  const handleValidateIp = async (isAuto = false) => {
    const formData = getFormData();
    const combinedIpRanges = formData.cidrBlocks;

    if (combinedIpRanges.length === 0) {
      if (!isAuto) {
        toast.error("Please enter at least one IP range or CIDR block.");
      }
      return false;
    }

    setIsValidatingIp(true);
    setCidrError("");
    try {
      const response = await axiosInstance.post(END_POINTS.VALIDATE_IP, {
        ip_range: combinedIpRanges,
      });

      if (response.status === 200 || response.status === 201) {
        const results = response.data?.data?.results || [];
        const invalidResult = results.find((r: any) => r.is_valid === false);

        if (invalidResult) {
          setCidrError(invalidResult.error || "Invalid CIDR format");

          return false;
        }

        if (!isAuto) {
          toast.success(response.data?.message || "IP validation successful");
        }
        setCidrError("");
        return true;
      }
      return false;
    } catch (error: any) {
      console.error("Error validating IP:", error);
      const errorData = error?.response?.data;
      if (errorData?.data?.within_limit === false) {
        setLimitPopupMessage(errorData?.message || "Total IP count exceeds limit");
        setIsLimitPopupOpen(true);
        setCidrError("");
        return false;
      } else if (error?.response?.status === 400) {
        setCidrError(errorData?.message || "Invalid IP address or CIDR range");
        return false;
      } else {
        if (!isAuto) {
          toast.error(errorData?.message || "Invalid IP address or CIDR range");
        }
        return false;
      }
    } finally {
      setIsValidatingIp(false);
    }
  };

  // const isOnPremPosture = scanType === "onprem-posture";
  // const isAwsScan =
  //   scanType === "aws-discovery" || scanType === "aws-discovery-posture";
  // const isOnPremDiscoveryOrPosture =
  //   scanType === "onprem-discovery" ||
  //   scanType === "onprem-discovery-posture" ||
  //   scanType === "onprem-posture";

  // Use API-fetched agents when available; otherwise fall back to props
  const filteredAgents = React.useMemo(() => {
    const source: AgentOption[] = (allAgents.length ? allAgents : agents) || [];
    return source.filter((agent) =>
      String(agent.status_display || "").trim().toLowerCase() === "active"
    );
  }, [allAgents, agents]);

  // Keep agentId in sync with available filtered agents
  React.useEffect(() => {
    if (location.pathname.includes("organization/agent")) return;
    if (!isAgentsLoading && agentId && !filteredAgents.some((a) => a.id === agentId)) {
      setAgentId("");
    }
  }, [filteredAgents, agentId, isAgentsLoading, location.pathname]);


  // Prevent API-only password type from leaking into SSH/Windows creator UI
  React.useEffect(() => {
    if (connectorType !== "api" && newCredPasswordType === "api_token") {
      setNewCredPasswordType("password");
    }
  }, [connectorType, newCredPasswordType]);

  const isNewCredentialValid = isCreatingCredential
    ? isAwsScan
      ? // AWS flows: Access Key, Secret Key, Account ID, and Credential Name
      newCredUsername.trim().length > 0 &&
      newCredSecret.trim().length > 0 &&
      newCredAccountId.trim().length > 0 &&
      newCredName.trim().length > 0
      : connectorType === "api"
        ? // On-prem API connector: (Password or API Token) + Credential Name
        newCredName.trim().length > 0 && newCredSecret.trim().length > 0
        : // On-prem SSH/Windows flows: Username + (Password or PEM) + Credential Name
        newCredUsername.trim().length > 0 &&
        newCredName.trim().length > 0 &&
        (newCredPasswordType === "password"
          ? newCredSecret.trim().length > 0
          : newCredPrivateKeyFileName.trim().length > 0)
    : false;

  const handlePpkUploadClick = () => {
    ppkInputRef.current?.click();
  };

  const handlePpkFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0] ?? null;

    if (!file) {
      setNewCredPrivateKeyFileName("");
      setNewCredPrivateKeyFile(null);
      setNewCredPrivateKeyError("");
      return;
    }

    const fileName = file.name.toLowerCase();

    if (!fileName.endsWith(".pem")) {
      setNewCredPrivateKeyFileName("");
      setNewCredPrivateKeyFile(null);
      setNewCredPrivateKeyError("Please upload .pem file.");
      if (ppkInputRef.current) {
        ppkInputRef.current.value = "";
      }
      return;
    }

    setNewCredPrivateKeyFileName(file.name);
    setNewCredPrivateKeyFile(file);
    setNewCredPrivateKeyError("");
  };

  const isTestDisabled =
    testStatus === "loading" ||
    testStatus === "success" ||
    (!isAwsScan && !agentId && !location.pathname.includes("organization/agent"));

  // On-Prem Posture validation
  const isPostureVendorCategoryValid = connectorType === "api" ? (!!selectedVendor && !!selectedCategory) : (selectedVendor ? !!selectedCategory : true);

  const isCredNotMandatory = scanType === "onprem-discovery" || scanType === "onprem-discovery-posture" || newCredPasswordType === "private_key";

  // Posture inline credential validity check
  const isPostureCredValid = isOnPremPosture && connectorType !== "file-upload"
    ? credentialId !== "" ||
    (connectorType === "api"
      ? newCredName.trim().length > 0 && newCredSecret.trim().length > 0
      : newCredUsername.trim().length > 0 &&
      (newCredPasswordType === "password"
        ? newCredSecret.trim().length > 0
        : newCredPrivateKeyFileName.trim().length > 0))
    : true;

  const partiallyFilledRows = postureRows.filter(
    (r) => r.assetIp.trim().length > 0 || r.credentialId
  );

  const validRows = partiallyFilledRows.filter((row) => {
    const isIpValid = row.assetIp.trim().length > 0;
    const isVendorCategoryValid =
      connectorType === "api"
        ? !!row.vendorId && !!row.categoryId
        : row.vendorId
          ? !!row.categoryId
          : true;
    const isCredValid =
      row.credentialId === "__create_new__"
        ? connectorType === "api"
          ? (row.newCredName || "").trim().length > 0 &&
            (row.newCredSecret || "").trim().length > 0
          : (row.newCredUsername || "").trim().length > 0 &&
            (row.newCredName || "").trim().length > 0 &&
            ((row.newCredPasswordType || "password") === "password"
              ? (row.newCredSecret || "").trim().length > 0
              : (row.newCredPrivateKeyFileName || "").trim().length > 0)
        : !!row.credentialId;
    return isIpValid && isVendorCategoryValid && isCredValid;
  });
  const isMultiIpRowsValid =
    validRows.length > 0 && validRows.length === partiallyFilledRows.length;

  const isAgentMissing = !agentId && !location.pathname.includes("organization/agent");
  const isAgentRequired = !isOnPremPosture;
  const isAgentInvalid = isAgentRequired && isAgentMissing;

  const isOnPremConnectDisabled = isAgentInvalid || isConnecting || (
    isOnPremPosture
      ? (connectorType === "file-upload"
        ? postureUploadFiles.length === 0 || invalidFilesList.length > 0 || isUploadingFiles || !eventId
        : isMultiIpMode
          ? !isMultiIpRowsValid
          : !isPostureCredValid || !assetIp || !isPostureVendorCategoryValid)
      : (!isCredNotMandatory && (!credentialId || credentialId === "__create_new__") && !isNewCredentialValid)
  );

  // For AWS / other scans: require Test success first
  const isCidrRequired =
    (scanType === "onprem-discovery" || scanType === "onprem-discovery-posture");

  const isCidrMissing = isCidrRequired && cidrBlocks.length === 0 && !cidrInput.trim();

  const isCredValid = isCredNotMandatory || (isOnPremPosture && (connectorType === "file-upload" || isMultiIpMode)) || (credentialId && credentialId !== "__create_new__") || isNewCredentialValid;

  // Derived schedule frequency helpers used by the schedule UI
  const _freqObj = scheduleOptions?.frequency?.find((f: any) => f.id === frequency);
  const isHourlyFrequency = !!(_freqObj && _freqObj.name === "Hourly");
  const isWeeklyFrequency = !!(_freqObj && _freqObj.name === "Weekly");
  const isOtherFrequency = !!(frequency && !isHourlyFrequency && !isWeeklyFrequency);

  const isEffectiveScheduled = isScheduled && !(isOnPremPosture && connectorType === "file-upload");

  const isPastTime = React.useMemo(() => {
    if (!isEffectiveScheduled || !frequency) return false;
    const now = dayjs();

    const getH24 = (hStr: string, ampm: string) => {
      let h = parseInt(hStr);
      if (isNaN(h)) return null;
      if (ampm === "PM" && h < 12) h += 12;
      if (ampm === "AM" && h === 12) h = 0;
      return h;
    };

    if (isOtherFrequency && onDate) {
      const h24 = getH24(selectedHour, selectedAmPm);
      const m = parseInt(selectedMinute);
      if (h24 === null || isNaN(m)) return false;
      const selected = dayjs(onDate, "DD-MM-YYYY").hour(h24).minute(m).second(0).millisecond(0);
      return selected.isBefore(now) || selected.isSame(now);
    }

    if (isWeeklyFrequency && selectedDay) {
      const h24 = getH24(selectedHour, selectedAmPm);
      const m = parseInt(selectedMinute);
      if (h24 === null || isNaN(m)) return false;

      const days = scheduleOptions?.Days || [];
      const dayObj = days.find((d: any) => d.id === selectedDay);
      if (dayObj) {
        const currentDayName = now.format("dddd");
        if (dayObj.name === currentDayName) {
          const selected = now.hour(h24).minute(m).second(0).millisecond(0);
          return selected.isBefore(now) || selected.isSame(now);
        }
      }
    }

    if (isHourlyFrequency) {
      const h24 = getH24(hourlyInterval, selectedAmPm);
      const m = parseInt(hourlyMinute);
      if (h24 === null || isNaN(m)) return false;
      const selected = now.hour(h24).minute(m).second(0).millisecond(0);
      return selected.isBefore(now) || selected.isSame(now);
    }

    return false;
  }, [
    isEffectiveScheduled,
    frequency,
    isOtherFrequency,
    onDate,
    selectedHour,
    selectedMinute,
    selectedAmPm,
    isWeeklyFrequency,
    selectedDay,
    scheduleOptions?.Days,
    isHourlyFrequency,
    hourlyInterval,
    hourlyMinute,
  ]);

  const isScheduleValid = isEffectiveScheduled
    ? !!(
      frequency &&
      (isHourlyFrequency
        ? hourlyInterval && hourlyMinute && selectedAmPm
        : isWeeklyFrequency
          ? selectedDay && selectedHour && selectedMinute && selectedAmPm
          : isOtherFrequency
            ? onDate && selectedHour && selectedMinute && selectedAmPm
            : false)
    ) && !isPastTime
    : true;

  const isConnectDisabled =
    isCidrMissing ||
    (!isCredValid && !isAwsRole) ||
    !isScheduleValid ||
    !!intervalError ||
    !!chunkError ||
    !!cidrError ||
    (isAwsScan && !isAwsRole && (!newCredAccountId.trim() || !!formik.errors.accountId)) ||
    (isAwsRole && (awsAccountIds.length === 0 || awsRoleStatus !== "success")) ||
    (isOnPremDiscoveryOrPosture
      ? isOnPremConnectDisabled
      : (!isAwsRole && !credentialId && !isNewCredentialValid) ||
      (isAgentUsed && !agentId && !location.pathname.includes("organization/agent"))) ||
    isConnecting;

  // (filtered vendors and posture-form validity derived values were removed —
  // they were unused in render; keep getFilteredVendors available if needed)


  // Which credentials to show based on scan type
  const displayCredentials = isOnPremPosture ? credentials : isAwsScan ? credentials : sshCredentials;

  const renderConnectorFields = () => {
    if (!isOnPremPosture) return null;

    if (connectorType === "file-upload") {
      return (
        <div
          className="nsd-upload-card"
          onDrop={handlePostureUploadDrop}
          onDragOver={handlePostureUploadDragOver}
          onClick={handlePostureChooseFilesClick}
          role="button"
          tabIndex={0}
        >
          {postureUploadFiles.length > 0 && (
            <button
              type="button"
              className="nsd-upload-add-more-corner"
              onClick={(e) => {
                e.stopPropagation();
                postureUploadInputRef.current?.click();
              }}
            >
              Add More
            </button>
          )}
          <div className="nsd-upload-inner">
            <div className="nsd-upload-icon">
              <img
                src={isDarkMode ? UploadImgDark : UploadImg}
                alt="Upload"
                style={{ width: 56, height: 56 }}
              />
            </div>
            <div className="nsd-upload-title">Upload files </div>
            <div className="nsd-upload-subtitle">
              Drag and drop your files here or{" "}
              <span
                className="nsd-upload-click"
                onClick={(e) => {
                  e.stopPropagation();
                  postureUploadInputRef.current?.click();
                }}
              >
                Choose files
              </span>
            </div>
            <div className="nsd-upload-hint">
              (Supported file formats - XML / JSON / TXT)
            </div>

            <button
              type="button"
              className="nsd-upload-link"
              onClick={(e) => {
                e.stopPropagation();
                SSHService.fetchFileData("config");
              }}
            >
              Download sample file
            </button>

            <input
              ref={postureUploadInputRef}
              type="file"
              accept="text/xml,application/xml,application/json,text/plain,.xml,.json,.txt"
              multiple
              style={{ display: "none" }}
              onChange={handlePostureUploadInputChange}
            />
          </div>
          {postureUploadError && (
            <div className="nsd-upload-error">{postureUploadError}</div>
          )}
          {postureUploadFiles.length > 0 && (
            <div className="nsd-upload-files">
              <div className="nsd-upload-files-header">
                <span>
                  {postureUploadFiles.length} file
                  {postureUploadFiles.length > 1 ? "s" : ""} selected
                </span>
                <button
                  type="button"
                  className="nsd-upload-files-clear"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPostureUploadFiles([]);
                    setInvalidFilesList([]);
                    setEventId(null);
                  }}
                >
                  Clear
                </button>
              </div>
              <ul className="nsd-upload-files-list">
                {postureUploadFiles.map((file) => (
                  <li key={file.name} className="nsd-upload-file-item" onClick={(e) => e.stopPropagation()}>
                    <div className="nsd-upload-file-row">
                      <div className="nsd-upload-file-icon" />
                      <div className={`nsd-upload-file-name ${invalidFilesList.includes(file.name) ? "error" : ""}`}>{file.name}</div>
                      <button
                        type="button"
                        className="nsd-upload-file-remove"
                        onClick={() => {
                          const fileName = file.name;
                          setPostureUploadFiles((prev) =>
                            prev.filter((p) => p.name !== fileName)
                          );
                          setInvalidFilesList((prev) =>
                            prev.filter((name) => name !== fileName)
                          );
                          if (postureUploadFiles.length <= 1) {
                            setEventId(null);
                          }
                        }}
                        aria-label={`Remove ${file.name}`}
                      >
                        ×
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }

    const commonPostureFields = (
      <>
        <div className="nsd-field-row">
          <TextField
            required
            label="Asset IP/URL"
            value={assetIp}
            onChange={_handleAssetIpChange}
            error={!!_assetIpError}
            helperText={_assetIpError}
            variant="outlined"
            size="small"
            fullWidth
            sx={fieldSx}
          />
        </div>
        <div className="nsd-field-row">
          <FormControl sx={{ maxWidth: "280px" }} size="small">
            <InputLabel>Vendor{connectorType === "api" ? "" : "(Optional)"}</InputLabel>
            <Select
              value={selectedVendor || ""}
              label={`Vendor${connectorType === "api" ? "" : "(Optional)"}`}
              onChange={_handleVendorChange}
              sx={selectSx}
            >
              {vendorsList.map((v: any) => {
                const vName = v.name.toLowerCase();

                if (
                  connectorType === "api" &&
                  (vName === "paloalto" ||
                    vName === "palo alto" ||
                    vName === "fortinet" ||
                    vName === "forti" ||
                    vName === "fortigate" ||
                    vName === "sophos")
                ) {
                  return (
                    <MenuItem key={v.id} value={v.id}>
                      {v.name}
                    </MenuItem>
                  );
                }

                if (connectorType === "ssh" && vName !== "microsoft") {
                  return (
                    <MenuItem key={v.id} value={v.id}>
                      {v.name}
                    </MenuItem>
                  );
                }

                if (connectorType === "windows" && vName === "microsoft") {
                  return (
                    <MenuItem key={v.id} value={v.id}>
                      {v.name}
                    </MenuItem>
                  );
                }

                return null;
              })}
            </Select>
          </FormControl>
          {selectedVendor && (


            <FormControl sx={{ maxWidth: "280px" }} size="small">
              <InputLabel required={!!selectedVendor}>Category</InputLabel>
              <Select
                value={selectedCategory || ""}
                label="Category"
                onChange={(e) => setSelectedCategory(e.target.value)}
                sx={selectSx}
              >
                {_categories.map((c: any) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>
        <div className="nsd-field-row" style={{ alignItems: 'center' }}>
          <FormControlLabel
            control={<Checkbox checked={customPort} onChange={(e) => setCustomPort(e.target.checked)} />}
            label="Custom Port"
            sx={{ '& .MuiTypography-root': { fontSize: 14 } }}
          />
          {customPort && (
            <TextField
              label="Port Number"
              value={customPortNumber}
              onChange={(e) => {
                if (/^\d*$/.test(e.target.value)) setCustomPortNumber(e.target.value);
              }}
              variant="outlined"
              size="small"
              fullWidth
              sx={fieldSx}
            />
          )}
        </div>
      </>
    );

    const renderCredentialForm = (isSubrow: boolean = false, rowId?: string) => {
      const isRowSpecific = isSubrow && rowId;
      const row = isRowSpecific ? postureRows.find(r => r.id === rowId) : null;
      const currentPasswordType = isRowSpecific ? (row?.newCredPasswordType || "password") : newCredPasswordType;
      const currentUsername = isRowSpecific ? (row?.newCredUsername || "") : newCredUsername;
      const currentSecret = isRowSpecific ? (row?.newCredSecret || "") : newCredSecret;
      const currentName = isRowSpecific ? (row?.newCredName || "") : newCredName;
      const currentSave = isRowSpecific ? (row?.newCredSave ?? true) : newCredSave;
      const currentExecPasswordEnabled = isRowSpecific ? (row?.newCredExecPasswordEnabled || false) : newCredExecPasswordEnabled;
      const currentAutoAssign = isRowSpecific ? (row?.newCredAutoAssign || false) : newCredAutoAssign;
      const currentExecPasswordText = isRowSpecific ? (row?.newCredExecPasswordText || "") : newCredExecPasswordText;
      const currentPrivateKeyFileName = isRowSpecific ? (row?.newCredPrivateKeyFileName || "") : newCredPrivateKeyFileName;

      const setPasswordType = (val: any) => isRowSpecific ? updatePostureRow(rowId!, "newCredPasswordType", val) : setNewCredPasswordType(val);
      const setUsername = (val: any) => isRowSpecific ? updatePostureRow(rowId!, "newCredUsername", val) : setNewCredUsername(val);
      const setSecret = (val: any) => isRowSpecific ? updatePostureRow(rowId!, "newCredSecret", val) : setNewCredSecret(val);
      const setName = (val: any) => isRowSpecific ? updatePostureRow(rowId!, "newCredName", val) : setNewCredName(val);
      const setSave = (val: any) => isRowSpecific ? updatePostureRow(rowId!, "newCredSave", val) : setNewCredSave(val);
      const setExecPasswordEnabled = (val: any) => isRowSpecific ? updatePostureRow(rowId!, "newCredExecPasswordEnabled", val) : setNewCredExecPasswordEnabled(val);
      const setAutoAssign = (val: any) => isRowSpecific ? updatePostureRow(rowId!, "newCredAutoAssign", val) : setNewCredAutoAssign(val);
      const setExecPasswordText = (val: any) => isRowSpecific ? updatePostureRow(rowId!, "newCredExecPasswordText", val) : setNewCredExecPasswordText(val);

      return (
      <div className="nsd-credential-card" style={isSubrow ? { marginLeft: '32px', marginTop: '8px', marginBottom: '16px' } : undefined}>
        <div className="nsd-credential-card-header">
          <div className="nsd-credential-card-title">Create New Credential</div>
        </div>
        <div className="nsd-credential-card-body">
          <div>
            <Typography sx={{ mb: 0.5, fontSize: 13, fontWeight: 600 }}>
              Password Type
            </Typography>
            <FormControl component="fieldset" size="small">
              <RadioGroup
                row
                value={currentPasswordType}
                onChange={(e) => {
                  const next = e.target.value as "password" | "api_token" | "private_key";
                  setPasswordType(next);
                  setUsername("");
                  setSecret("");
                  setNewCredShowPassword(false);
                }}
              >
                <FormControlLabel
                  value="password"
                  control={<Radio size="small" />}
                  label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Password</Typography>}
                />
                {connectorType === "api" && (
                  <FormControlLabel
                    value="api_token"
                    control={<Radio size="small" />}
                    label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>API Token</Typography>}
                  />
                )}
                {connectorType === "ssh" && (
                  <FormControlLabel
                    value="private_key"
                    control={<Radio size="small" />}
                    label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Private Key</Typography>}
                  />
                )}
              </RadioGroup>
            </FormControl>
          </div>

          {currentPasswordType === "api_token" && (
            <TextField
              label="API Token"
              value={currentSecret}
              onChange={(e) => setSecret(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              sx={fieldSx}
            />
          )}

          {currentPasswordType !== "api_token" && (
            <TextField
              label="Username"
              value={currentUsername}
              onChange={(e) => setUsername(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              sx={fieldSx}
            />
          )}

          {currentPasswordType === "private_key" && (
            <>
              <div className="nsd-pem-upload-box">
                <input
                  type="file"
                  accept=".pem"
                  ref={ppkInputRef}
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    if (!file) {
                      if (isRowSpecific) {
                        updatePostureRow(rowId!, "newCredPrivateKeyFileName", "");
                        updatePostureRow(rowId!, "newCredPrivateKeyFile", null);
                      } else {
                        setNewCredPrivateKeyFileName("");
                        setNewCredPrivateKeyFile(null);
                      }
                      setNewCredPrivateKeyError("");
                      return;
                    }
                    const fileName = file.name.toLowerCase();
                    if (!fileName.endsWith(".pem")) {
                      if (isRowSpecific) {
                        updatePostureRow(rowId!, "newCredPrivateKeyFileName", "");
                        updatePostureRow(rowId!, "newCredPrivateKeyFile", null);
                      } else {
                        setNewCredPrivateKeyFileName("");
                        setNewCredPrivateKeyFile(null);
                      }
                      setNewCredPrivateKeyError("Please upload .pem file.");
                      if (ppkInputRef.current) ppkInputRef.current.value = "";
                      return;
                    }
                    if (isRowSpecific) {
                      updatePostureRow(rowId!, "newCredPrivateKeyFileName", file.name);
                      updatePostureRow(rowId!, "newCredPrivateKeyFile", file);
                    } else {
                      setNewCredPrivateKeyFileName(file.name);
                      setNewCredPrivateKeyFile(file);
                    }
                    setNewCredPrivateKeyError("");
                  }}
                  style={{ display: "none" }}
                />
                <Button
                  variant="contained"
                  onClick={handlePpkUploadClick}
                  sx={{
                    backgroundColor: "#3A2AdA",
                    color: "white",
                    "&:hover": { backgroundColor: "#3225b7" },
                  }}
                >
                  Upload PEM File
                </Button>
                {currentPrivateKeyFileName && (
                  <Typography variant="body2" sx={{ mt: 1, fontSize: 12, color: "text.secondary" }}>
                    Selected: {currentPrivateKeyFileName}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mt: 1, fontSize: 12, color: "text.secondary" }}>
                  If a PPK file is available, it should be converted into a PEM file
                </Typography>
                {newCredPrivateKeyError && (
                  <Typography variant="body2" color="error" sx={{ mt: 1, fontSize: 12 }}>
                    {newCredPrivateKeyError}
                  </Typography>
                )}
              </div>

              <FormControlLabel
                control={
                  <IOSSwitch
                    checked={currentExecPasswordEnabled}
                    onChange={(e) => setExecPasswordEnabled(e.target.checked)}
                    sx={{ mr: 2 }}
                  />
                }
                label={<Typography sx={{ fontSize: 15, fontWeight: 500, lineHeight: "16px" }}>Exec Password</Typography>}
                labelPlacement="start"
                sx={{ justifyContent: "space-between", width: "100%", mb: 1 }}
              />

              {currentExecPasswordEnabled && (
                <TextField
                  label="Exec Password"
                  type={newCredShowExecPassword ? "text" : "password"}
                  value={currentExecPasswordText}
                  onChange={(e) => setExecPasswordText(e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={fieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {newCredShowExecPassword ? (
                          <Visibility onClick={() => setNewCredShowExecPassword((prev) => !prev)} sx={{ cursor: "pointer" }} />
                        ) : (
                          <VisibilityOff onClick={() => setNewCredShowExecPassword((prev) => !prev)} sx={{ cursor: "pointer" }} />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </>
          )}

          {currentPasswordType === "password" && (
            <>
              <TextField
                label="Password"
                type={newCredShowPassword ? "text" : "password"}
                value={currentSecret}
                onChange={(e) => setSecret(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                sx={fieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {newCredShowPassword ? (
                        <Visibility onClick={() => setNewCredShowPassword((prev) => !prev)} sx={{ cursor: "pointer" }} />
                      ) : (
                        <VisibilityOff onClick={() => setNewCredShowPassword((prev) => !prev)} sx={{ cursor: "pointer" }} />
                      )}
                    </InputAdornment>
                  ),
                }}
              />

              {(connectorType === "ssh" || connectorType === "windows") && (
                <div className="nsd-credential-toggle-column" style={{ marginTop: '16px' }}>
                  <FormControlLabel
                    control={
                      <IOSSwitch
                        checked={currentExecPasswordEnabled}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setExecPasswordEnabled(checked);
                          if (checked) {
                            setAutoAssign(false);
                          }
                        }}
                        sx={{ mr: 2 }}
                      />
                    }
                    label={<Typography sx={{ fontSize: 15, fontWeight: 500, lineHeight: "16px" }}>Exec Password</Typography>}
                    labelPlacement="start"
                    sx={{ justifyContent: "space-between", width: "100%", mb: 1 }}
                  />

                  <Typography sx={{ fontSize: 15, fontWeight: 500, lineHeight: "16px", ml: 2, mb: 1 }}>
                    Auto Assign
                  </Typography>

                  <FormControlLabel
                    control={
                      <IOSSwitch
                        checked={currentAutoAssign}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setAutoAssign(checked);
                          if (checked) {
                            setExecPasswordEnabled(false);
                            setExecPasswordText("");
                          }
                        }}
                        sx={{ mr: 2 }}
                      />
                    }
                    label={
                      <Typography sx={{ fontWeight: 400, fontSize: 12, maxWidth: 350, lineHeight: "15px" }}>
                        This will auto-assign Login Password as Exec Password when missing.
                      </Typography>
                    }
                    labelPlacement="start"
                    sx={{ mb: 1, justifyContent: "space-between", width: "100%" }}
                  />
                </div>
              )}

              {currentExecPasswordEnabled && (
                <TextField
                  label="Exec Password"
                  type={newCredShowExecPassword ? "text" : "password"}
                  value={currentExecPasswordText}
                  onChange={(e) => setExecPasswordText(e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={fieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {newCredShowExecPassword ? (
                          <Visibility onClick={() => setNewCredShowExecPassword((prev) => !prev)} sx={{ cursor: "pointer" }} />
                        ) : (
                          <VisibilityOff onClick={() => setNewCredShowExecPassword((prev) => !prev)} sx={{ cursor: "pointer" }} />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={currentSave}
                onChange={(e) => setSave(e.target.checked)}
                sx={{ color: "#3a2ada", "&.Mui-checked": { color: "#3a2ada" } }}
              />
            }
            label="Save Credential"
          />

          <TextField
            label="Credential Name"
            value={currentName}
            onChange={(e) => setName(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            sx={fieldSx}
            required
          />
        </div>
      </div>
    );
  };

    const renderSshUploadToggle = () => (
      <div className="nsd-multi-ip-header" style={{ justifyContent: "flex-end" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span className="nsd-ssh-upload-label">SSH Upload</span>
          <IOSSwitch
            checked={isSshUploadMode}
            onChange={(e) => {
              const checked = e.target.checked;
              setIsSshUploadMode(checked);
              setPostureUploadFiles([]);
              setInvalidFilesList([]);
              setEventId(null);
              setCredentialId("");
              setIsCreatingCredential(false);
              resetNewCredentialFields();
              setPostureRows([emptyPostureRow("1", connectorType)]);
            }}
            sx={{ ml: 1 }}
          />
        </div>
      </div>
    );

    if (connectorType === "api" || connectorType === "ssh") {
      return (
        <>
          {renderSshUploadToggle()}

          {isMultiIpMode ? (
            <MultiIpSshUpload
              isMultiIpMode={isMultiIpMode}
              setIsMultiIpMode={setIsMultiIpMode}
              isSshUploadMode={isSshUploadMode}
              setIsSshUploadMode={setIsSshUploadMode}
              postureRows={postureRows}
              addPostureRow={addPostureRow}
              updatePostureRow={updatePostureRow}
              removePostureRow={removePostureRow}
              handlePostureRowKeyDown={handlePostureRowKeyDown}
              postureUploadFiles={postureUploadFiles}
              setPostureUploadFiles={setPostureUploadFiles}
              invalidFilesList={invalidFilesList}
              setInvalidFilesList={setInvalidFilesList}
              postureUploadError={postureUploadError}
              setEventId={setEventId}
              handlePostureUploadDrop={handlePostureUploadDrop}
              handlePostureUploadDragOver={handlePostureUploadDragOver}
              handlePostureChooseFilesClick={handlePostureChooseFilesClick}
              postureUploadInputRef={postureUploadInputRef}
              handlePostureUploadInputChange={handlePostureUploadInputChange}
              vendorsList={vendorsList}
              credentials={credentials}
              connectorType={connectorType}
              isDarkMode={isDarkMode}
              renderCredentialForm={renderCredentialForm}
              credentialId={credentialId}
              setCredentialId={handleCredentialChange}
              masterTemplates={masterTemplates}
              onClearCsvImport={() => {
                setPostureUploadFiles([]);
                setInvalidFilesList([]);
                setEventId(null);
                setPostureRows([emptyPostureRow("1", connectorType)]);
              }}
              applyBulkToAllRows={applyBulkToAllRows}
            />
          ) : (
            /* Single IP Mode fields */
            <>
              {commonPostureFields}
              <div className="nsd-field-row">
                <FormControl fullWidth size="small">
                  <InputLabel>Select Credential</InputLabel>
                  <Select
                    value={credentialId}
                    label="Select Credential"
                    onChange={(e) => handleCredentialChange(e.target.value)}
                    sx={selectSx}
                    renderValue={(val: any) => {
                      if (!val) return "";
                      if (val === "__create_new__") return "Create New Credential";
                      const cred = credentials.find((c) => c.id === val);
                      return cred?.name || String(val);
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: { maxHeight: 320 },
                        className: "scroll",
                      },
                    }}
                  >
                    <MenuItem value="__create_new__">
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <AddCircleOutlineIcon sx={{ color: "#3a2ada", fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Create New Credential"
                        primaryTypographyProps={{ sx: { color: "#3a2ada", fontWeight: 600, fontSize: 14 } }}
                      />
                    </MenuItem>
                    {credentials.map((cred) => (
                      <MenuItem key={cred.id} value={cred.id}>{cred.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              {/* Inline Create New Credential card */}
              {isCreatingCredential && renderCredentialForm(false)}
            </>
          )}
        </>
      );
    }

    if (connectorType === "windows") {
      const renderWindowsCredentialForm = (isSubrow: boolean = false, rowId?: string) => {
        const isRowSpecific = isSubrow && rowId;
        const row = isRowSpecific ? postureRows.find(r => r.id === rowId) : null;
        
        const currentPasswordType = isRowSpecific ? (row?.newCredPasswordType || "password") : newCredPasswordType;
        const currentUsername = isRowSpecific ? (row?.newCredUsername || "") : newCredUsername;
        const currentSecret = isRowSpecific ? (row?.newCredSecret || "") : newCredSecret;
        const currentName = isRowSpecific ? (row?.newCredName || "") : newCredName;
        const currentSave = isRowSpecific ? (row?.newCredSave ?? true) : newCredSave;
        const currentExecPasswordEnabled = isRowSpecific ? (row?.newCredExecPasswordEnabled || false) : newCredExecPasswordEnabled;
        const currentAutoAssign = isRowSpecific ? (row?.newCredAutoAssign || false) : newCredAutoAssign;
        const currentExecPasswordText = isRowSpecific ? (row?.newCredExecPasswordText || "") : newCredExecPasswordText;
        const currentPrivateKeyFileName = isRowSpecific ? (row?.newCredPrivateKeyFileName || "") : newCredPrivateKeyFileName;

        const setPasswordType = (val: any) => isRowSpecific ? updatePostureRow(rowId!, "newCredPasswordType", val) : setNewCredPasswordType(val);
        const setUsername = (val: any) => isRowSpecific ? updatePostureRow(rowId!, "newCredUsername", val) : setNewCredUsername(val);
        const setSecret = (val: any) => isRowSpecific ? updatePostureRow(rowId!, "newCredSecret", val) : setNewCredSecret(val);
        const setName = (val: any) => isRowSpecific ? updatePostureRow(rowId!, "newCredName", val) : setNewCredName(val);
        const setSave = (val: any) => isRowSpecific ? updatePostureRow(rowId!, "newCredSave", val) : setNewCredSave(val);
        const setExecPasswordEnabled = (val: any) => isRowSpecific ? updatePostureRow(rowId!, "newCredExecPasswordEnabled", val) : setNewCredExecPasswordEnabled(val);
        const setAutoAssign = (val: any) => isRowSpecific ? updatePostureRow(rowId!, "newCredAutoAssign", val) : setNewCredAutoAssign(val);
        const setExecPasswordText = (val: any) => isRowSpecific ? updatePostureRow(rowId!, "newCredExecPasswordText", val) : setNewCredExecPasswordText(val);

        return (
          <div style={isSubrow ? { marginLeft: '32px', marginTop: '8px', marginBottom: '16px' } : undefined}>
            <CredentialCreator
              useLabels={isOnPremPosture}
              isAwsScan={isAwsScan}
              newCredUsername={currentUsername}
              setNewCredUsername={setUsername}
              newCredSecret={currentSecret}
              setNewCredSecret={setSecret}
              newCredShowPassword={newCredShowPassword}
              setNewCredShowPassword={setNewCredShowPassword}
              newCredPasswordType={currentPasswordType === "api_token" ? "password" : currentPasswordType}
              setNewCredPasswordType={setPasswordType}
              newCredPrivateKeyFileName={currentPrivateKeyFileName}
              newCredPrivateKeyError={newCredPrivateKeyError}
              ppkInputRef={ppkInputRef}
              handlePpkUploadClick={handlePpkUploadClick}
              handlePpkFileChange={(e) => {
                  const file = e.target.files?.[0] ?? null;
                  if (!file) {
                    if (isRowSpecific) {
                      updatePostureRow(rowId!, "newCredPrivateKeyFileName", "");
                      updatePostureRow(rowId!, "newCredPrivateKeyFile", null);
                    } else {
                      setNewCredPrivateKeyFileName("");
                      setNewCredPrivateKeyFile(null);
                    }
                    setNewCredPrivateKeyError("");
                    return;
                  }
                  const fileName = file.name.toLowerCase();
                  if (!fileName.endsWith(".pem")) {
                    if (isRowSpecific) {
                      updatePostureRow(rowId!, "newCredPrivateKeyFileName", "");
                      updatePostureRow(rowId!, "newCredPrivateKeyFile", null);
                    } else {
                      setNewCredPrivateKeyFileName("");
                      setNewCredPrivateKeyFile(null);
                    }
                    setNewCredPrivateKeyError("Please upload .pem file.");
                    if (ppkInputRef.current) ppkInputRef.current.value = "";
                    return;
                  }
                  if (isRowSpecific) {
                    updatePostureRow(rowId!, "newCredPrivateKeyFileName", file.name);
                    updatePostureRow(rowId!, "newCredPrivateKeyFile", file);
                  } else {
                    setNewCredPrivateKeyFileName(file.name);
                    setNewCredPrivateKeyFile(file);
                  }
                  setNewCredPrivateKeyError("");
              }}
              newCredExecPasswordEnabled={currentExecPasswordEnabled}
              setNewCredExecPasswordEnabled={setExecPasswordEnabled}
              newCredShowExecPassword={newCredShowExecPassword}
              setNewCredShowExecPassword={setNewCredShowExecPassword}
              newCredExecPasswordText={currentExecPasswordText}
              setNewCredExecPasswordText={setExecPasswordText}
              newCredAutoAssign={currentAutoAssign}
              setNewCredAutoAssign={setAutoAssign}
              newCredSave={currentSave}
              setNewCredSave={setSave}
              newCredName={currentName}
              setNewCredName={setName}
              newCredAccountId={newCredAccountId}
              setNewCredAccountId={setNewCredAccountId}
              newCredDescription={newCredDescription}
              setNewCredDescription={setNewCredDescription}
              fieldSx={fieldSx}
            />
          </div>
        );
      };

      return (
        <>
          {renderSshUploadToggle()}

          {isMultiIpMode ? (
            <MultiIpSshUpload
              isMultiIpMode={isMultiIpMode}
              setIsMultiIpMode={setIsMultiIpMode}
              isSshUploadMode={isSshUploadMode}
              setIsSshUploadMode={setIsSshUploadMode}
              postureRows={postureRows}
              addPostureRow={addPostureRow}
              updatePostureRow={updatePostureRow}
              removePostureRow={removePostureRow}
              handlePostureRowKeyDown={handlePostureRowKeyDown}
              postureUploadFiles={postureUploadFiles}
              setPostureUploadFiles={setPostureUploadFiles}
              invalidFilesList={invalidFilesList}
              setInvalidFilesList={setInvalidFilesList}
              postureUploadError={postureUploadError}
              setEventId={setEventId}
              handlePostureUploadDrop={handlePostureUploadDrop}
              handlePostureUploadDragOver={handlePostureUploadDragOver}
              handlePostureChooseFilesClick={handlePostureChooseFilesClick}
              postureUploadInputRef={postureUploadInputRef}
              handlePostureUploadInputChange={handlePostureUploadInputChange}
              vendorsList={vendorsList}
              credentials={credentials}
              connectorType={connectorType}
              isDarkMode={isDarkMode}
              renderCredentialForm={renderWindowsCredentialForm}
              credentialId={credentialId}
              setCredentialId={handleCredentialChange}
              masterTemplates={masterTemplates}
              onClearCsvImport={() => {
                setPostureUploadFiles([]);
                setInvalidFilesList([]);
                setEventId(null);
                setPostureRows([emptyPostureRow("1", connectorType)]);
              }}
              applyBulkToAllRows={applyBulkToAllRows}
            />
          ) : (
            /* Single IP Mode fields */
            <>
              {commonPostureFields}
              <div className="nsd-field-row">
                <FormControl fullWidth size="small">
                  <InputLabel>Select Credential</InputLabel>
                  <Select
                    value={credentialId}
                    label="Select Credential"
                    onChange={(e) => handleCredentialChange(e.target.value)}
                    sx={selectSx}
                    renderValue={(val: any) => {
                      if (!val) return "";
                      if (val === "__create_new__") return "Create New Credential";
                      const cred = credentials.find((c) => c.id === val);
                      return cred?.name || String(val);
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: { maxHeight: 320 },
                        className: "scroll",
                      },
                    }}
                  >
                    <MenuItem value="__create_new__">
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <AddCircleOutlineIcon sx={{ color: "#3a2ada", fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Create New Credential"
                        primaryTypographyProps={{ sx: { color: "#3a2ada", fontWeight: 600, fontSize: 14 } }}
                      />
                    </MenuItem>
                    {credentials.map((cred) => (
                      <MenuItem key={cred.id} value={cred.id}>{cred.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              {/* Inline Create New Credential card */}
              {isCreatingCredential && renderWindowsCredentialForm(false)}
            </>
          )}
        </>
      );
    }

    return null;
  };

  /* ── Render ── */

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        sx={{
          "& .MuiDrawer-paper": {
            boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
          },
          "& .MuiBackdrop-root": {
            backdropFilter: "none",
            backgroundColor: "rgba(0,0,0,0.3)",
          },
        }}
      >
        <div className={`new-scan-drawer ${modeClass} ${isOnPremDiscoveryOrPosture ? 'onprem-drawer' : ''}`}>
          {/* ── Header ── */}
          <div className="nsd-header">
            <div className="nsd-header-left">
              {!isFromScheduleView && (
                <span
                  className="nsd-back-btn"
                  onClick={onBack || handleClose}
                  role="button"
                  tabIndex={0}
                >
                  <ArrowBackIcon fontSize="small" />
                </span>
              )}
              <h3 className="nsd-title">{isFromScheduleView ? "Create Schedule" : "New Scans"}</h3>
            </div>
            <span
              className="nsd-close-btn"
              onClick={handleClose}
              role="button"
              tabIndex={0}
            >
              <CancelIcon />
            </span>
          </div>

          {/* ── Body ── */}
          <div className="nsd-body">
            {/* Row 1: Scan Name + Description */}
            <div className="nsd-field-row">
              <TextField
                label="Scan Name"
                value={scanName}
                onChange={(e) => setScanName(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                sx={fieldSx}
              />
              <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                sx={fieldSx}
              />
            </div>

            {/* Row 2: Scan Type + Connector (On-Prem Posture) or Agent (Discovery / AWS) */}
            <div className="nsd-field-row">
              <FormControl fullWidth size="small">
                <InputLabel>Scan Type</InputLabel>
                <Select
                  value={scanType}
                  label="Scan Type"
                  onChange={(e) =>
                    handleScanTypeChange(e.target.value as ScanTypeId)
                  }
                  sx={selectSx}
                  renderValue={(val) => {
                    const opt = filteredScanTypeOptions.find((o) => o.id === val);
                    return opt?.label || val;
                  }}
                >
                  {filteredScanTypeOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      <div className="nsd-scan-type-option">
                        <span>{opt.label}</span>
                        <Tooltip title={SCAN_TYPE_DESCRIPTIONS[opt.id] || ""} placement="top" arrow>
                          <InfoOutlinedIcon className="nsd-info-icon" />
                        </Tooltip>
                      </div>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {isOnPremPosture ? (
                <FormControl fullWidth size="small">
                  <InputLabel>Connector</InputLabel>
                  <Select
                    value={connectorType}
                    label="Connector"
                    onChange={(e) =>
                      handleConnectorChange(e.target.value as ConnectorType)
                    }
                    sx={selectSx}
                    renderValue={(val) => {
                      const opt = CONNECTOR_OPTIONS.find((o) => o.id === val);
                      return opt?.label || val;
                    }}
                  >
                    {CONNECTOR_OPTIONS.filter((opt) => !(location.pathname.includes("organization/agent") && opt.id === "file-upload")).map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                isAgentUsed && !location.pathname.includes("organization/agent") && (
                  <FormControl fullWidth size="small">
                    <InputLabel>Agent</InputLabel>
                    <Select
                      value={agentId}
                      label="Agent"
                      onChange={(e) => handleAgentChange(e.target.value)}
                      sx={{
                        ...selectSx,
                        ...(agentId
                          ? { "& .MuiSelect-icon": { display: "none" } }
                          : {}),
                      }}
                      disabled={isAgentsLoading}
                      MenuProps={{
                        PaperProps: {
                          style: { maxHeight: 320 },
                          className: "scroll",
                        },
                      }}
                      renderValue={(selected) => {
                        if (!selected) return "";
                        const agent = filteredAgents.find((a) => a.id === selected);
                        return agent?.name || String(selected);
                      }}
                      endAdornment={
                        agentId ? (
                          <InputAdornment position="end" sx={{ mr: 1.5 }}>
                            <IconButton
                              size="small"
                              aria-label="Clear agent"
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAgentChange("");
                              }}
                              edge="end"
                            >
                              <ClearIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </InputAdornment>
                        ) : undefined
                      }
                    >
                      {isAgentsLoading ? (
                        <MenuItem disabled>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Loading agents...
                        </MenuItem>
                      ) : filteredAgents.length === 0 ? (
                        <MenuItem disabled>No active agents</MenuItem>
                      ) : (
                        filteredAgents.map((agent) => (
                          <MenuItem key={agent.id} value={agent.id}>
                            {agent.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                )
              )}
            </div>

            {/* AWS: how Spark logs in to the account */}
            {!isOnPremPosture && isAwsScan && (
              <RadioGroup
                row
                value={awsConnection}
                onChange={(e) => {
                  const next = e.target.value as "assume_role" | "access_keys";
                  setAwsConnection(next);
                  setAwsRoleStatus("idle");
                  if (next === "assume_role") {
                    setIsCreatingCredential(false);
                    setCredentialId("");
                    if (/^\d{12}$/.test(newCredAccountId.trim()) && awsAccountIds.length === 0) {
                      setAwsAccountIds([newCredAccountId.trim()]);
                    }
                  } else if (awsAccountIds.length > 0 && !newCredAccountId.trim()) {
                    // Access keys belong to one account: keep the first one.
                    setNewCredAccountId(awsAccountIds[0]);
                    formik.setFieldValue("accountId", awsAccountIds[0]);
                  }
                }}
                sx={{ marginBottom: "8px" }}
              >
                <FormControlLabel
                  value="assume_role"
                  control={<Radio size="small" />}
                  label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Connect with IAM role (recommended)</Typography>}
                />
                <FormControlLabel
                  value="access_keys"
                  control={<Radio size="small" />}
                  label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Access keys</Typography>}
                />
              </RadioGroup>
            )}

            {/* AWS through the IAM role: specific accounts only for now. Scanning a whole AWS
                Organization (scope "organization", AwsRoleConnection) is on hold. */}

            {/* AWS access keys: Account ID | Select Credential (the IAM role card has its own field
                for one or more account IDs) */}
            {!isOnPremPosture && isAwsScan && !isAwsRole && (
              <div className="nsd-field-row" style={{ marginBottom: "16px" }}>
                <TextField
                  label="Account ID"
                  value={newCredAccountId}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 12);
                    setNewCredAccountId(val);
                    formik.setFieldValue("accountId", val);
                    if (awsRoleStatus !== "idle") setAwsRoleStatus("idle");
                  }}
                  onBlur={formik.handleBlur}
                  name="accountId"
                  error={formik.touched.accountId && Boolean(formik.errors.accountId)}
                  helperText={formik.touched.accountId && formik.errors.accountId}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={fieldSx}
                  required
                  inputProps={{
                    maxLength: 12,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                />
                {!isAwsRole && (
                <FormControl fullWidth size="small">
                  <InputLabel>Select Credential</InputLabel>
                  <Select
                    value={credentialId}
                    label="Select Credential"
                    onChange={(e) => handleCredentialChange(e.target.value)}
                    sx={selectSx}
                    renderValue={(val: any) => {
                      if (!val) return "";
                      if (val === "__create_new__") return "Create New Credential";
                      const cred = displayCredentials.find((c) => c.id === val);
                      return cred?.name || String(val);
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: { maxHeight: 320 },
                        className: "scroll",
                      },
                    }}
                  >
                    <MenuItem value="__create_new__">
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <AddCircleOutlineIcon sx={{ color: "#3a2ada", fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Create New Credential"
                        primaryTypographyProps={{ sx: { color: "#3a2ada", fontWeight: 600, fontSize: 14 } }}
                      />
                    </MenuItem>
                    {displayCredentials.map((cred) => (
                      <MenuItem key={cred.id} value={cred.id}>{cred.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                )}
              </div>
            )}

            {!isOnPremPosture && isAwsRole && (
              <AwsRoleConnection
                scope={awsScope}
                accountIds={awsAccountIds}
                onAccountIdsChange={setAwsAccountIds}
                roleArn={awsRoleArn}
                onRoleArnChange={setAwsRoleArn}
                status={awsRoleStatus}
                onStatusChange={setAwsRoleStatus}
                fieldSx={fieldSx}
              />
            )}


            {/* Agent row for On-Prem Posture – hidden for File Upload */}
            {isOnPremPosture && connectorType !== "file-upload" && (
              <div className="nsd-field-row">
                {!location.pathname.includes("organization/agent") && (
                  <FormControl fullWidth size="small" className="nsd-field-half">
                    <InputLabel>Agent</InputLabel>
                    <Select
                      value={agentId}
                      label="Agent"
                      onChange={(e) => handleAgentChange(e.target.value)}
                      sx={selectSx}
                      disabled={isAgentsLoading}
                      MenuProps={{
                        PaperProps: {
                          style: { maxHeight: 320 },
                          className: "scroll",
                        },
                      }}
                    >
                      {isAgentsLoading ? (
                        <MenuItem disabled>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Loading agents...
                        </MenuItem>
                      ) : filteredAgents.length === 0 ? (
                        <MenuItem disabled>No active agents</MenuItem>
                      ) : (
                        filteredAgents.map((agent) => (
                          <MenuItem key={agent.id} value={agent.id}>
                            {agent.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                )}
              </div>
            )}

            {/* CIDR Block section – hidden for Posture File Upload and API */}
            {(scanType === "onprem-discovery" ||
              scanType === "onprem-discovery-posture" ||
              (scanType !== "onprem-posture" && scanType !== "aws-discovery-posture" && scanType !== "aws-discovery")) && (
                <div className="nsd-cidr-section">
                  <div className="nsd-cidr-header">
                    <div className="nsd-cidr-title">Enter CIDR Block</div>

                  </div>

                  <div className="nsd-cidr-input-wrapper" style={{ color: "#534F7A", fontWeight: 500, fontSize: "14px" }}>
                    Press Enter after each CIDR block to add multiple entries.

                    <div className="nsd-cidr-input-box">
                      {cidrBlocks.map((block) => (
                        <span
                          key={block}
                          className="nsd-cidr-chip"
                          onClick={() => handleRemoveCidr(block)}
                        >
                          {block}
                          <span
                            className="nsd-cidr-chip-remove"
                            aria-hidden="true"
                          >

                          </span>
                        </span>
                      ))}
                      <input
                        ref={cidrInputRef}
                        className="nsd-cidr-input"
                        value={cidrInput}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (/^[0-9./, ]*$/.test(val)) {
                            setCidrInput(val);
                            if (cidrError) setCidrError("");
                          }
                        }}
                        onKeyDown={handleCidrKeyDown}
                        placeholder={(cidrBlocks.length === 0 && !cidrInput.trim()) ? "e.g. 192.168.1.0/24 — Press comma to separate the CIDR Block" : ""}
                        required
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => { handleValidateIp(false); }}
                        disabled={isValidatingIp || (cidrBlocks.length === 0 && !cidrInput.trim())}
                        startIcon={isValidatingIp ? <CircularProgress size={16} color="inherit" /> : null}
                      >
                        {isValidatingIp ? "Validating..." : "Validate"}
                      </Button>
                    </div>
                    {cidrError && (
                      <Typography
                        sx={{ fontSize: "13px", fontWeight: 500, color: "red" }}
                      >
                        {cidrError}
                      </Typography>
                    )}
                    {cidrInput && (

                      <div className="nsd-field-row" style={{ marginTop: 10 }}>
                        <div style={{ flex: 1 }}>

                          <TextField
                            label="Chunk Size"
                            value={chunk}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (/^\d*$/.test(val)) {
                                setChunk(val);
                                if (val && parseInt(val, 10) < 1) {
                                  setChunkError("Minimum 1 chunk size is required");
                                } else {
                                  setChunkError("");
                                }
                              }
                            }}
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={fieldSx}
                            error={!!chunkError}
                          />
                          {chunkError && (
                            <Typography
                              color="error"
                              sx={{ mt: 0.5, fontSize: "11px", fontWeight: 500 }}
                            >
                              {chunkError}
                            </Typography>
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <TextField
                            label="Interval in minutes"
                            value={interval}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (/^\d*$/.test(val)) {
                                setInterval(val);
                                if (val && parseInt(val, 10) < 1) {
                                  setIntervalError("Minimum interval required is 1 minutes");
                                } else {
                                  setIntervalError("");
                                }
                              }
                            }}
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={fieldSx}
                            error={!!intervalError}
                          />
                          {intervalError && (
                            <Typography
                              color="error"
                              sx={{ mt: 0.5, fontSize: "11px", fontWeight: 500 }}
                            >
                              {intervalError}
                            </Typography>
                          )}
                        </div>

                      </div>
                    )}
                  </div>

                  {/* Multi-select Credentials */}
                  {(scanType === "onprem-discovery" || scanType === "onprem-discovery-posture") && (
                    <FormControl fullWidth sx={{ mt: 2 }}>
                      <InputLabel id="select-credentials-label">Select Credentials</InputLabel>
                      <Select
                        labelId="select-credentials-label"
                        id="select-credentials"
                        multiple
                        value={selectedCredentials}
                        onChange={(e) => {
                          const value = e.target.value;
                          const selected = typeof value === "string" ? value.split(",") : value;
                          if (selected.includes("all")) {
                            if (selectedCredentials.length === sshCredentials.length) {
                              setSelectedCredentials([]);
                            } else {
                              setSelectedCredentials(sshCredentials.map((c) => c.id));
                            }
                          } else {
                            setSelectedCredentials(selected);
                          }
                        }}
                        input={<OutlinedInput label="Select Credentials" />}
                        renderValue={(selected) => (
                          <Box
                            id="new-scan-creds-box"
                            className="scroll"
                            sx={{
                              maxHeight: "34px",
                              overflowY: "auto",
                              pr: 0.5
                            }}
                          >
                            <Box 
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                                pb: 0.5
                              }}
                            >
                            {selected.map((credId) => {
                              const cred = sshCredentials.find((c) => c.id === credId);
                              return (
                                <Chip
                                  key={credId}
                                  label={cred?.name || credId}
                                  size="small"
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onDelete={() =>
                                    setSelectedCredentials((prev) =>
                                      prev.filter((id) => id !== credId)
                                    )
                                  }
                                />
                              );
                            })}
                            </Box>
                          </Box>
                        )}
                        sx={{ ...selectSx, height: "auto", minHeight: "40px" }}
                        MenuProps={{
                          sx: { mt: 1 },
                          variant: "menu",
                          anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "left",
                          },
                          transformOrigin: {
                            vertical: "top",
                            horizontal: "left",
                          },
                          PaperProps: { 
                            style: { maxHeight: 250, marginTop: 8, overflowY: "auto", overflowX: "hidden" },
                            className: "scroll" 
                          },
                          slotProps: {
                            backdrop: {
                              onWheel: (e: any) => {
                                const box = document.getElementById("new-scan-creds-box");
                                if (box) {
                                  const rect = box.getBoundingClientRect();
                                  if (
                                    e.clientX >= rect.left && e.clientX <= rect.right &&
                                    e.clientY >= rect.top && e.clientY <= rect.bottom
                                  ) {
                                    box.scrollTop += e.deltaY;
                                  }
                                }
                              }
                            } as any
                          }
                        }}
                      >
                        {isCredentialsLoading ? (
                          <MenuItem disabled>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Loading credentials...
                          </MenuItem>
                        ) : sshCredentials.length === 0 ? (
                          <MenuItem disabled>No credentials found</MenuItem>
                        ) : (
                          [
                            <MenuItem 
                              key="select-all" 
                              value="all"
                              sx={{
                                borderBottom: `1px solid ${isDarkMode ? "#4A4A52" : "#C0C5CE"}`,
                                mb: 0.5,
                                pb: 1,
                                pt: 1,
                              }}
                            >
                              <Checkbox
                                checked={sshCredentials.length > 0 && selectedCredentials.length === sshCredentials.length}
                                size="small"
                              />
                              <ListItemText 
                                primary="Select All"
                                primaryTypographyProps={{ 
                                  fontWeight: 600,
                                  color: isDarkMode ? "#E5E7EB" : "#101828"
                                }}
                              />
                            </MenuItem>,
                            ...sshCredentials.map((cred) => (
                              <MenuItem key={cred.id} value={cred.id}>
                                <Checkbox checked={selectedCredentials.includes(cred.id)} size="small" />
                                <ListItemText primary={cred.name} />
                              </MenuItem>
                            ))
                          ]
                        )}
                      </Select>
                    </FormControl>
                  )}
                </div>
              )}



            {/* Inline "Create New Credential" card – AWS uses Access Key/ID, others use Username+Password */}
            {!isOnPremPosture &&
              isCreatingCredential && (
                <div className="nsd-credential-card">
                  <div className="nsd-credential-card-header">
                    <div className="nsd-credential-card-title">Create New Credential</div>
                  </div>
                  <div className="nsd-credential-card-body">
                    {isAwsScan ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                        <TextField
                          label="Credential Name"
                          value={newCredName}
                          onChange={(e) => setNewCredName(e.target.value)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          sx={fieldSx}
                          required
                        />
                        <TextField
                          label="Description (Optional)"
                          value={newCredDescription}
                          onChange={(e) => setNewCredDescription(e.target.value)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          sx={fieldSx}
                        />
                        <TextField
                          label="Access Key"
                          value={newCredUsername}
                          onChange={(e) => setNewCredUsername(e.target.value)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          sx={fieldSx}
                          required
                        />
                        <TextField
                          label="Secret Key"
                          value={newCredSecret}
                          onChange={(e) => setNewCredSecret(e.target.value)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          sx={fieldSx}
                          required
                        />
                      </div>
                    ) : (
                      <>
                        <TextField
                          label="Username"
                          value={newCredUsername}
                          onChange={(e) => setNewCredUsername(e.target.value)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          sx={fieldSx}
                        />

                        <div>
                          <Typography sx={{ mb: 0.5, fontSize: 13, fontWeight: 600 }}>
                            Password Type
                          </Typography>
                          <FormControl component="fieldset" size="small">
                            <RadioGroup
                              row
                              value={newCredPasswordType}
                              onChange={(e) =>
                                setNewCredPasswordType(
                                  e.target.value as "password" | "private_key"
                                )
                              }
                            >
                              <FormControlLabel
                                value="password"
                                control={<Radio size="small" />}
                                label={
                                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                                    Password
                                  </Typography>
                                }
                              />
                              <FormControlLabel
                                value="private_key"
                                control={<Radio size="small" />}
                                label={
                                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                                    Private Key
                                  </Typography>
                                }
                              />
                            </RadioGroup>
                          </FormControl>
                        </div>

                        {newCredPasswordType === "private_key" && (
                          <>
                            <div className="nsd-pem-upload-box">
                              <input
                                type="file"
                                accept=".pem"
                                ref={ppkInputRef}
                                onChange={handlePpkFileChange}
                                style={{ display: "none" }}
                              />
                              <Button
                                variant="contained"
                                onClick={handlePpkUploadClick}
                                sx={{
                                  backgroundColor: "#3A2AdA",
                                  color: "white",
                                  "&:hover": {
                                    backgroundColor: "#3225b7",
                                  },
                                }}
                              >
                                Upload PEM File
                              </Button>
                              {newCredPrivateKeyFileName && (
                                <Typography
                                  variant="body2"
                                  sx={{ mt: 1, fontSize: 12, color: "text.secondary" }}
                                >
                                  Selected: {newCredPrivateKeyFileName}
                                </Typography>
                              )}
                              <Typography
                                variant="body2"
                                sx={{ mt: 1, fontSize: 12, color: "text.secondary" }}
                              >
                                If a PPK file is available, it should be converted into a
                                PEM file
                              </Typography>
                              {newCredPrivateKeyError && (
                                <Typography
                                  variant="body2"
                                  color="error"
                                  sx={{ mt: 1, fontSize: 12 }}
                                >
                                  {newCredPrivateKeyError}
                                </Typography>
                              )}
                            </div>

                            <FormControlLabel
                              control={
                                <IOSSwitch
                                  checked={newCredExecPasswordEnabled}
                                  onChange={(e) =>
                                    setNewCredExecPasswordEnabled(e.target.checked)
                                  }
                                  sx={{ mr: 2 }}
                                />
                              }
                              label={
                                <Typography
                                  sx={{
                                    fontSize: 15,
                                    fontWeight: 500,
                                    lineHeight: "16px",
                                  }}
                                >
                                  Exec Password
                                </Typography>
                              }
                              labelPlacement="start"
                              sx={{
                                justifyContent: "space-between",
                                width: "100%",
                                mb: 1,
                              }}
                            />

                            {newCredExecPasswordEnabled && (
                              <TextField
                                label="Exec Password"
                                type={newCredShowExecPassword ? "text" : "password"}
                                value={newCredExecPasswordText}
                                onChange={(e) =>
                                  setNewCredExecPasswordText(e.target.value)
                                }
                                variant="outlined"
                                size="small"
                                fullWidth
                                sx={fieldSx}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      {newCredShowExecPassword ? (
                                        <Visibility
                                          onClick={() =>
                                            setNewCredShowExecPassword((prev) => !prev)
                                          }
                                          sx={{ cursor: "pointer" }}
                                        />
                                      ) : (
                                        <VisibilityOff
                                          onClick={() =>
                                            setNewCredShowExecPassword((prev) => !prev)
                                          }
                                          sx={{ cursor: "pointer" }}
                                        />
                                      )}
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            )}
                          </>
                        )}

                        {newCredPasswordType === "password" && (
                          <>
                            <TextField
                              label="Password"
                              type={newCredShowPassword ? "text" : "password"}
                              value={newCredSecret}
                              onChange={(e) => setNewCredSecret(e.target.value)}
                              variant="outlined"
                              size="small"
                              fullWidth
                              sx={fieldSx}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    {newCredShowPassword ? (
                                      <Visibility
                                        onClick={() =>
                                          setNewCredShowPassword((prev) => !prev)
                                        }
                                        sx={{ cursor: "pointer" }}
                                      />
                                    ) : (
                                      <VisibilityOff
                                        onClick={() =>
                                          setNewCredShowPassword((prev) => !prev)
                                        }
                                        sx={{ cursor: "pointer" }}
                                      />
                                    )}
                                  </InputAdornment>
                                ),
                              }}
                            />

                            <div className="nsd-credential-toggle-column">
                              <FormControlLabel
                                control={
                                  <IOSSwitch
                                    checked={newCredExecPasswordEnabled}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setNewCredExecPasswordEnabled(checked);
                                      if (checked) {
                                        setNewCredAutoAssign(false);
                                      }
                                    }}
                                    sx={{ mr: 2 }}
                                  />
                                }
                                label={
                                  <Typography
                                    sx={{
                                      fontSize: 15,
                                      fontWeight: 500,
                                      lineHeight: "16px",
                                    }}
                                  >
                                    Exec Password
                                  </Typography>
                                }
                                labelPlacement="start"
                                sx={{
                                  justifyContent: "space-between",
                                  width: "100%",
                                  mb: 1,
                                }}
                              />

                              <Typography
                                sx={{
                                  fontSize: 15,
                                  fontWeight: 500,
                                  lineHeight: "16px",
                                  ml: 2,
                                  mb: 1,
                                }}
                              >
                                Auto Assign
                              </Typography>

                              <FormControlLabel
                                control={
                                  <IOSSwitch
                                    checked={newCredAutoAssign}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setNewCredAutoAssign(checked);
                                      if (checked) {
                                        setNewCredExecPasswordEnabled(false);
                                        setNewCredExecPasswordText("");
                                      }
                                    }}
                                    sx={{ mr: 2 }}
                                  />
                                }
                                label={
                                  <Typography
                                    sx={{
                                      fontWeight: 400,
                                      fontSize: 12,
                                      maxWidth: 350,
                                      lineHeight: "15px",
                                    }}
                                  >
                                    This will auto-assign Login Password as Exec Password
                                    when missing.
                                  </Typography>
                                }
                                labelPlacement="start"
                                sx={{
                                  mb: 1,
                                  justifyContent: "space-between",
                                  width: "100%",
                                }}
                              />
                            </div>

                            {newCredExecPasswordEnabled && (
                              <TextField
                                label="Exec Password"
                                type={newCredShowExecPassword ? "text" : "password"}
                                value={newCredExecPasswordText}
                                onChange={(e) =>
                                  setNewCredExecPasswordText(e.target.value)
                                }
                                variant="outlined"
                                size="small"
                                fullWidth
                                sx={fieldSx}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      {newCredShowExecPassword ? (
                                        <Visibility
                                          onClick={() =>
                                            setNewCredShowExecPassword((prev) => !prev)
                                          }
                                          sx={{ cursor: "pointer" }}
                                        />
                                      ) : (
                                        <VisibilityOff
                                          onClick={() =>
                                            setNewCredShowExecPassword((prev) => !prev)
                                          }
                                          sx={{ cursor: "pointer" }}
                                        />
                                      )}
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            )}
                          </>
                        )}
                      </>
                    )}

                    {!isAwsScan && (
                      <>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={newCredSave}
                              onChange={(e) => setNewCredSave(e.target.checked)}
                              sx={{
                                color: "#3a2ada",
                                "&.Mui-checked": { color: "#3a2ada" },
                              }}
                            />
                          }
                          label="Save Credential"
                        />

                        <TextField
                          label="Credential Name"
                          value={newCredName}
                          onChange={(e) => setNewCredName(e.target.value)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          sx={fieldSx}
                          required
                        />
                      </>
                    )}
                  </div>
                </div>
              )}

            {/* Connector-specific fields for On-Prem Posture */}
            {renderConnectorFields()}

            {/* Schedule for scan - hidden for File Upload connector */}
            {!(isOnPremPosture && connectorType === "file-upload") && (
              <div className="nsd-schedule-section">
                <div className="nsd-schedule-header">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isScheduled}
                        onChange={(e) => setIsScheduled(e.target.checked)}
                        disabled={isFromScheduleView}
                        sx={{
                          color: "#3a2ada",
                          "&.Mui-checked": { color: "#3a2ada" },
                        }}
                      />
                    }
                    label=""
                    sx={{ mr: 0 }}
                  />
                  <div className="nsd-schedule-text">
                    <div className="nsd-schedule-title">Schedule for scan</div>
                    <div className="nsd-schedule-subtitle">
                      Run later or on a recurring basis instead of starting now.
                      Pick a date and frequency after enabling.
                    </div>
                  </div>
                </div>

                {isScheduled && (
                  <div className="nsd-schedule-fields">
                    {/* Frequency */}
                    <FormControl fullWidth size="small" required>
                      <InputLabel>Frequency</InputLabel>
                      <Select
                        value={frequency}
                        label="Frequency"
                        onChange={(e) => {
                          const value = String(e.target.value);
                          setFrequency(value);
                          setOnDate(null);
                          setScheduleDate("");
                          setSelectedDay("");
                          setSelectedHour("");
                          setSelectedMinute("");
                          setSelectedAmPm("");
                          setHourlyInterval("");
                          setHourlyMinute("");
                          setAtTime("");
                        }}
                        sx={selectSx}
                      >
                        {scheduleOptions?.frequency?.map((opt: any) => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* On Date (for non-weekly/hourly) */}
                    {isOtherFrequency && (
                      <div className="nsd-time-row">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="On Date"
                            value={onDate}
                            onChange={handleScheduleDateChange}
                            disablePast
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                                sx: fieldSx,
                                required: true,
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </div>
                    )}

                    {/* Weekly: On Day */}
                    {isWeeklyFrequency && (
                      <FormControl fullWidth size="small" sx={{ mt: 2 }} required>
                        <InputLabel>On Day</InputLabel>
                        <Select
                          value={selectedDay}
                          label="On Day"
                          onChange={(e) => setSelectedDay(String(e.target.value))}
                          sx={selectSx}
                        >
                          {scheduleOptions?.Days?.map((day: any) => (
                            <MenuItem key={day.id} value={day.id}>
                              {day.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    {/* Time inputs */}
                    {isHourlyFrequency ? (
                      <div className="nsd-time-row">
                        <TextField
                          label="Hour"
                          value={hourlyInterval}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/\D/g, "");
                            const num = parseInt(raw, 10);
                            if (raw === "" || (num >= 1 && num <= 24)) {
                              setHourlyInterval(raw);
                            }
                          }}
                          type="text"
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            min: 1,
                            max: 24,
                            maxLength: 2,
                          }}
                          required
                          fullWidth
                          sx={fieldSx}
                        />
                        <TextField
                          label="Minutes"
                          value={hourlyMinute}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/\D/g, "");
                            const num = parseInt(raw, 10);
                            if (raw === "" || (num >= 0 && num <= 59)) {
                              setHourlyMinute(raw);
                            }
                          }}
                          type="text"
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            min: 0,
                            max: 59,
                            maxLength: 2,
                          }}
                          required
                          fullWidth
                          sx={fieldSx}
                        />
                        <FormControl size="small" sx={{ minWidth: 120 }} required>
                          <InputLabel>AM/PM</InputLabel>
                          <Select
                            value={selectedAmPm}
                            label="AM/PM"
                            onChange={(e) => {
                              const value = String(e.target.value);
                              setSelectedAmPm(value);
                              const h = hourlyInterval || "1";
                              const m = (hourlyMinute || "0").padStart(2, "0");
                              setAtTime(`${h}:${m} ${value}`);
                            }}
                            sx={selectSx}
                          >
                            <MenuItem value=""></MenuItem>
                            <MenuItem value="AM">AM</MenuItem>
                            <MenuItem value="PM">PM</MenuItem>
                          </Select>
                        </FormControl>
                      </div>
                    ) : (
                      <div className="nsd-time-row">
                        <TextField
                          label="Hour"
                          value={selectedHour}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/\D/g, "");
                            const num = parseInt(raw, 10);
                            if (raw === "" || (num >= 1 && num <= 12)) {
                              setSelectedHour(raw);
                              const minute = (selectedMinute || "0").padStart(
                                2,
                                "0"
                              );
                              const period = selectedAmPm || "AM";
                              if (raw) {
                                setAtTime(`${raw}:${minute} ${period}`);
                              } else {
                                setAtTime("");
                              }
                            }
                          }}
                          type="text"
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            min: 1,
                            max: 12,
                            maxLength: 2,
                          }}
                          required
                          fullWidth
                          sx={fieldSx}
                        />
                        <TextField
                          label="Minutes"
                          value={selectedMinute}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/\D/g, "");
                            const num = parseInt(raw, 10);
                            if (raw === "" || (num >= 0 && num <= 59)) {
                              setSelectedMinute(raw);
                              const hour = selectedHour || "12";
                              const period = selectedAmPm || "AM";
                              if (raw) {
                                const m = raw.padStart(2, "0");
                                setAtTime(`${hour}:${m} ${period}`);
                              } else {
                                setAtTime("");
                              }
                            }
                          }}
                          type="text"
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            min: 0,
                            max: 59,
                            maxLength: 2,
                          }}
                          required
                          fullWidth
                          sx={fieldSx}
                        />
                        <FormControl size="small" sx={{ minWidth: 120 }} required>
                          <InputLabel>AM/PM</InputLabel>
                          <Select
                            value={selectedAmPm}
                            label="AM/PM"
                            onChange={(e) => {
                              const value = String(e.target.value);
                              setSelectedAmPm(value);
                              const hour = selectedHour || "12";
                              const minute = (selectedMinute || "0").padStart(
                                2,
                                "0"
                              );
                              if (hour) {
                                setAtTime(`${hour}:${minute} ${value}`);
                              } else {
                                setAtTime("");
                              }
                            }}
                            sx={selectSx}
                          >
                            <MenuItem value=""></MenuItem>
                            <MenuItem value="AM">AM</MenuItem>
                            <MenuItem value="PM">PM</MenuItem>
                          </Select>
                        </FormControl>
                      </div>
                    )}
                    {isPastTime && (
                      <Typography
                        sx={{ mt: 1, fontSize: "12px", fontWeight: 500, color: "red" }}
                      >
                        Scheduled time cannot be in the past or current time.
                      </Typography>
                    )}
                  </div>
                )}
              </div>
            )}
            {/* Connection status banner (from Test) */}
            {testStatus === "error" && (
              <div className="nsd-status nsd-status-error">
                <div className="nsd-status-icon" aria-hidden="true" />
                <div className="nsd-status-text">
                  <div className="nsd-status-title">Connection Failed</div>
                  <div className="nsd-status-subtitle">
                    Authentication failed. Check your credentials.
                  </div>
                </div>
              </div>
            )}

            {testStatus === "success" && (
              <div className="nsd-status nsd-status-success">
                <div className="nsd-status-icon" aria-hidden="true">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="10"
                      cy="10"
                      r="8"
                      fill="#ffffff"
                      stroke="#16A34A"
                      strokeWidth="2"
                    />
                    <path
                      d="M7.2 10.4L9 12.2L12.8 8.4"
                      stroke="#16A34A"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="nsd-status-text">
                  <div className="nsd-status-title">Connection Successful</div>
                  <div className="nsd-status-subtitle">
                    Authentication failed. Check your credentials.
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Footer ── */}
          <div className="nsd-footer">
            <Button
              className="nsd-btn-test"
              sx={{
                display: "block",
                ml: "auto",
                mr: 3,
                mb: 2,
                width: "120px",
                height: "35px",
                backgroundColor: "#3a2ada",
              }}
              onClick={() => {
                handleConnect();
              }}
              disabled={isConnectDisabled}
            >
              {isConnecting ? (
                <>
                  <CircularProgress
                    size={16}
                    sx={{ mr: 1, color: "inherit" }}
                  />
                  Submitting…
                </>
              ) : (
                "Connect"
              )}
            </Button>
          </div>
        </div>
      </Drawer >

      <Dialog
        open={isLimitPopupOpen}
        onClose={() => setIsLimitPopupOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            padding: "8px",
            width: "350px",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "18px", textAlign: "center" }}>
          IP Limit Exceeded
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "14px", }}>
            Large IP range detected.
            Processing this request may take significantly longer than usual. Do you want to continue?    </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: "12px", pb: 2 }}>
          <Button
            onClick={() => {
              setIsLimitPopupOpen(false);
              setTimeout(() => cidrInputRef.current?.focus(), 100);
            }}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderColor: "#3a2ada",
              color: "#3a2ada",
              "&:hover": { borderColor: "#3225b7", },
            }}
          >
            Edit CIDR
          </Button>
          <Button
            onClick={() => {
              setIsLimitPopupOpen(false);
              setBypassLimit(true);
            }}
            variant="contained"
            sx={{
              textTransform: "none",
              backgroundColor: "#3a2ada",
              color: "#fff",
              "&:hover": { backgroundColor: "#3225b7" },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NewScanDrawer;
